import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api';
import usePermissions from '../hooks/usePermissions';
import {
  Modal, HrCardFields, DocButton, FilePreviewModal,
  PermissionModal, DetailPermissionModal,
} from './HRManagement';
import FileDropZone from '../components/FileDropZone';
import { SysMailGuideNote } from '../components/SysMailAddress';

const FIELD_LABEL = {
  name: '이름', position: '직급', company: '회사명', address: '주소',
  phone: '연락처', email: '이메일', emergency_phone: '긴급연락처'
};

// ── 계정관리(마이페이지) "관리자 권한부여" 카드를 회원 리스트로 개편(2026-08) ──────────
// 요청사항: "관리자 권한부여 자리에 회원 리스트를 보여줌 — 목록: 등록일, 사번, ...,
// 계정관리는 풀어서 목록에 추가, 권한설정". 예전에는 부서→직급→이름 드롭다운으로 대상 1명을
// 골라 관리자 권한만 부여/회수하는 좁은 폼이었는데, 이제 승인된 전체 회원을 표로 보여주고
// 각 행에서 부서/직급/회원구분 변경, 관리자 권한 부여/회수, 비밀번호 초기화, 권한설정까지
// 바로 처리한다(예전 인사관리 > 사원정보의 "계정관리" 모달 + "권한설정" 버튼 기능을 그대로
// 옮겨와 목록 칸으로 "풀어놓은" 것 — PermissionModal/DetailPermissionModal도 HRManagement.js
// 것을 그대로 재사용한다).
const memTh = { padding: '8px 10px', fontSize: 14, fontWeight: 700, color: '#666', background: '#f8fafc', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '1px solid #e5e7eb' };
const memTd = { padding: '8px 10px', fontSize: 14, textAlign: 'center', borderTop: '1px solid #f0f0f0', whiteSpace: 'nowrap' };
const memSelect = { fontSize: 14, padding: '4px 6px', border: '1px solid #ddd', borderRadius: 6, outline: 'none' };
const memMiniBtn = { padding: '3px 7px', fontSize: 11, fontWeight: 700, borderRadius: 6, border: '1px solid #185FA5', color: '#185FA5', background: '#fff', cursor: 'pointer' };
const memActionBtn = { background: '#fff', border: '1px solid', borderRadius: 7, padding: '5px 9px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };

// 2026-08 후속 요청: "구분을 회원가입 화면과 통일" — 회원가입(Login.js ACCOUNT_TYPE_OPTIONS)에는
// 사내임직원/협력사/생산현황판 전용 세 가지만 있는데, 이 화면은 그동안 HRManagement.js의 공용
// 회원구분(사내임직원/협력사/개인관리)을 그대로 가져다 썼다. "개인관리"는 공개범위 설정 전용의
// 별도 분류라 회원가입 화면과는 무관하므로, 계정관리 목록에서는 이 화면 전용으로 회원가입과
// 동일한 세 가지 값만 쓴다. "생산현황판 전용"은 실제로는 member_type이 아니라 계정 자체의
// account_type(display_only)으로 결정되므로 드롭다운으로 바꿀 수 없고(백엔드 /member-type은
// staff/partner/individual만 허용) 고정 표시만 한다.
const ACCT_TYPE_OPTIONS = [
  { value: 'staff',   label: '사내임직원' },
  { value: 'partner', label: '협력사' },
];
const toAcctType = (u) => (u?.member_type === 'partner' ? 'partner' : 'staff');

// 2026-08 후속 요청: "목록버튼을 클릭하면 오름차순/내림차순 적용" — HRManagement.js의
// useSort/SortTh와 동일한 패턴이지만 그쪽은 export되어 있지 않고 HRManagement 전용 th/C.primary
// 스타일에 묶여 있어서, 이 화면의 memTh 스타일에 맞춘 로컬 버전을 별도로 둔다.
function MemSortTh({ label, sortKey, currentKey, currentDir, onSort, align }) {
  if (!sortKey) return <th style={{ ...memTh, ...(align ? { textAlign: align } : {}) }}>{label}</th>;
  const active = currentKey === sortKey;
  return (
    <th style={{ ...memTh, ...(align ? { textAlign: align } : {}), cursor: 'pointer', userSelect: 'none', color: active ? '#185FA5' : memTh.color }}
      title={active ? (currentDir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      onClick={() => onSort(sortKey)}>
      {label}{active && (currentDir === 'asc' ? ' ▲' : ' ▼')}
    </th>
  );
}

// 2026-08 후속 요청: "구분, 등록일, 사번, 부서, 직급, 이름, 아이디, 권한설정, 관리자권한,
// 비밀번호 이렇게 구성" — 컬럼 순서를 이 순서로 재배열하고, 아이디를 이름 셀에 붙여쓰던 것을
// 별도 컬럼으로 분리한다. 또한 "회원가입이 발생하면 승인/거부를 목록에 나타나게" 요청에 따라
// 승인대기(approved===0) 회원도 이 목록에 함께 나오며, 그 행은 권한설정/관리자권한/비밀번호
// 3칸 자리에 승인/거부 버튼을 대신 보여준다(기존 인사관리 승인대기 처리와 동일한 API 재사용).
function MemberRow({ u, departments, positions, onChangeType, onChangeDept, onChangePos, onGrant, onRevoke, onResetPassword, onOpenPerm, onOpenLoginLog, onApprove, onReject, savingFieldId, grantingId, revokingId, resettingId, approvingId, rejectingId, canManageAdmin }) {
  const [typeDraft, setTypeDraft] = useState(toAcctType(u));
  const [deptDraft, setDeptDraft] = useState(u.department || '');
  const [posDraft, setPosDraft]   = useState(u.position || '');
  useEffect(() => {
    setTypeDraft(toAcctType(u)); setDeptDraft(u.department || ''); setPosDraft(u.position || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u.member_type, u.department, u.position]);

  const isAdmin = u.role === 'admin';
  const isPending = u.approved === 0;
  // 생산현황판 전용 계정(account_type==='display_only')은 회원가입 화면 기준으로는 "구분" 중
  // 하나이지만, 실제로는 member_type이 아니라 계정 자체의 성격이라 /member-type API로 바꿀 수
  // 없다(백엔드가 staff/partner/individual만 허용) — 드롭다운 대신 고정 배지로만 보여준다.
  const isDisplay = u.account_type === 'display_only';
  const savingType = savingFieldId === `${u.id}-type`;
  const savingDept = savingFieldId === `${u.id}-dept`;
  const savingPos  = savingFieldId === `${u.id}-pos`;

  return (
    <tr style={isPending ? { background: '#fffbeb' } : undefined}>
      <td style={memTd}>
        {isDisplay ? (
          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', whiteSpace: 'nowrap' }}>
            🖥️ 생산현황판 전용
          </span>
        ) : (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
            <select value={typeDraft} onChange={e => setTypeDraft(e.target.value)} style={{ ...memSelect, width: 100 }}>
              {ACCT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {typeDraft !== toAcctType(u) && (
              <button onClick={() => onChangeType(u, typeDraft)} disabled={savingType} style={memMiniBtn}>{savingType ? '···' : '✓'}</button>
            )}
          </div>
        )}
      </td>
      <td style={memTd}>{u.created_at?.slice(0, 10) || '-'}</td>
      <td style={memTd}>{u.employee_no || '-'}</td>
      <td style={memTd}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
          <select value={deptDraft} onChange={e => setDeptDraft(e.target.value)} style={{ ...memSelect, width: 100 }}>
            <option value="">미지정</option>
            {u.department && !departments.some(d => d.name === u.department) && (
              <option value={u.department}>{u.department}(목록외)</option>
            )}
            {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
          {deptDraft !== (u.department || '') && (
            <button onClick={() => onChangeDept(u, deptDraft)} disabled={savingDept} style={memMiniBtn}>{savingDept ? '···' : '✓'}</button>
          )}
        </div>
      </td>
      <td style={memTd}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
          <select value={posDraft} onChange={e => setPosDraft(e.target.value)} style={{ ...memSelect, width: 100 }}>
            <option value="">미지정</option>
            {u.position && !positions.some(p => p.name === u.position) && (
              <option value={u.position}>{u.position}(목록외)</option>
            )}
            {positions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
          {posDraft !== (u.position || '') && (
            <button onClick={() => onChangePos(u, posDraft)} disabled={savingPos} style={memMiniBtn}>{savingPos ? '···' : '✓'}</button>
          )}
        </div>
      </td>
      <td style={{ ...memTd, textAlign: 'left' }}>
        <b>{u.name}</b>
        {isAdmin && (
          <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8' }}>관리자</span>
        )}
        {isPending && (
          <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#b45309' }}>승인대기</span>
        )}
      </td>
      <td style={{ ...memTd, textAlign: 'left', color: '#888' }}>@{u.username}</td>
      {isPending ? (
        <td style={memTd} colSpan={4}>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            <button onClick={() => onApprove(u)} disabled={approvingId === u.id}
              style={{ ...memActionBtn, color: '#185FA5', borderColor: '#185FA5' }}>
              {approvingId === u.id ? '승인중...' : '✅ 승인'}
            </button>
            <button onClick={() => onReject(u)} disabled={rejectingId === u.id}
              style={{ ...memActionBtn, color: '#dc2626', borderColor: '#dc2626' }}>
              {rejectingId === u.id ? '거부중...' : '❌ 거부'}
            </button>
          </div>
        </td>
      ) : (
        <>
          <td style={memTd}>
            <button onClick={() => onOpenLoginLog(u)} style={{ ...memActionBtn, color: '#0e7490', borderColor: '#0e7490' }}>
              📡 접속로그
            </button>
          </td>
          <td style={memTd}>
            <button onClick={() => onOpenPerm(u)} style={{ ...memActionBtn, color: '#7c3aed', borderColor: '#7c3aed' }}>
              🔐 권한설정
            </button>
          </td>
          <td style={memTd}>
            {/* 관리자 권한 부여/회수는 최고관리자(ninegro, role==='superadmin')만 가능하도록
                백엔드(admin.js grant/revoke 라우트)에서 별도로 더 엄격하게 막고 있다 — 관리자
                권한을 부여받은 계정(canManageAdmin===false)에게는 눌러도 403이 나는 버튼을
                보여주지 않고 대신 안내 문구만 표시한다(2026-09 추가). */}
            {canManageAdmin ? (
              isAdmin ? (
                <button onClick={() => onRevoke(u)} disabled={revokingId === u.id}
                  style={{ ...memActionBtn, color: '#dc2626', borderColor: '#dc2626' }}>
                  {revokingId === u.id ? '회수중...' : '권한회수'}
                </button>
              ) : (
                <button onClick={() => onGrant(u)} disabled={grantingId === u.id}
                  style={{ ...memActionBtn, color: '#185FA5', borderColor: '#185FA5' }}>
                  {grantingId === u.id ? '부여중...' : '관리자지정'}
                </button>
              )
            ) : (
              <span style={{ fontSize: 11.5, color: '#bbb' }}>-</span>
            )}
          </td>
          <td style={memTd}>
            <button onClick={() => onResetPassword(u)} disabled={resettingId === u.id}
              style={{ ...memActionBtn, color: '#b45309', borderColor: '#b45309' }}>
              {resettingId === u.id ? '초기화중...' : '🔑 초기화'}
            </button>
          </td>
        </>
      )}
    </tr>
  );
}

// ── 접속로그 모달(계정관리 관리자모드) ──────────────────────────────────────────
// 2026-09 요청: "목록에서 아이디와 권한설정 사이에 접속로그 추가 — 버튼 클릭시 모달로
// 해당 아이디로 접속된 일자/시간 표시. 과거 로그도 서버에서 가져오고, 월별 조회 및
// 접속 IP(사내/사외 구분)도 함께 표기. 우측상단에 인쇄버튼 추가." 백엔드
// GET /admin/users/:id/login-logs?year=&month=(둘 다 선택)에서 그 계정의 로그인 기록을
// (일자/시간/로그아웃시간/IP/사내·사외) 그대로 내려주므로, 여기서는 년월 선택 UI와
// 표, 인쇄 버튼만 구성한다. 인쇄는 별도 팝업 창을 열어 표만 담은 간단한 HTML을
// document.write로 채우고 곧바로 print()하는 방식(이 코드베이스의 다른 화면들과
// 동일하게 서버측 PDF 생성 없이 브라우저 인쇄 다이얼로그를 그대로 활용).
function LoginLogModal({ user, onClose }) {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    API.get(`/admin/users/${user.id}/login-logs`, { params })
      .then(r => setLogs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [user.id, year, month]);

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  const handlePrint = () => {
    const rowsHtml = logs.map(l => `
      <tr>
        <td>${l.login_date || '-'}</td>
        <td>${(l.login_time || '-').replace(l.login_date ? l.login_date + ' ' : '', '')}</td>
        <td>${l.logout_time ? l.logout_time.replace(l.login_date ? l.login_date + ' ' : '', '') : '-'}</td>
        <td>${l.ip || '-'}</td>
        <td>${l.location || '-'}</td>
      </tr>`).join('');
    const win = window.open('', '_blank', 'width=720,height=900');
    if (!win) return;
    win.document.write(`
      <html><head><title>접속로그 - ${user.name}(@${user.username})</title>
      <style>
        body { font-family: 'Malgun Gothic', sans-serif; padding: 24px; }
        h2 { font-size: 16px; margin-bottom: 4px; }
        p { font-size: 12px; color: #666; margin-top: 0; }
        table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
        th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: center; }
        th { background: #f3f4f6; }
      </style></head>
      <body>
        <h2>접속로그 — ${user.name} (@${user.username})</h2>
        <p>조회 기간: ${year}년 ${month ? month + '월' : '전체'} · 총 ${logs.length}건</p>
        <table>
          <thead><tr><th>접속일자</th><th>접속시간</th><th>로그아웃시간</th><th>접속IP</th><th>구분</th></tr></thead>
          <tbody>${rowsHtml || '<tr><td colspan="5">조회된 접속기록이 없습니다.</td></tr>'}</tbody>
        </table>
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <Modal
      title={`📡 접속로그 — ${user.name} (@${user.username})`}
      onClose={onClose}
      width={620}
      headerActions={
        <button onClick={handlePrint} style={{ background: '#fff', border: '1px solid #185FA5', color: '#185FA5',
          borderRadius: 6, padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
          🖨️ 인쇄
        </button>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <select value={year} onChange={e => setYear(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
          {yearOptions.map(y => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select value={month} onChange={e => setMonth(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
          <option value="">전체</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
        </select>
        <span style={{ fontSize: 12, color: '#999', marginLeft: 'auto' }}>총 {logs.length}건</span>
      </div>
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={loginLogTh}>접속일자</th>
              <th style={loginLogTh}>접속시간</th>
              <th style={loginLogTh}>로그아웃시간</th>
              <th style={loginLogTh}>접속IP</th>
              <th style={loginLogTh}>구분</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ ...loginLogTd, color: '#aaa', padding: '20px 0' }}>불러오는 중...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} style={{ ...loginLogTd, color: '#aaa', padding: '20px 0' }}>조회된 접속기록이 없습니다.</td></tr>
            ) : (
              logs.map(l => (
                <tr key={l.id}>
                  <td style={loginLogTd}>{l.login_date || '-'}</td>
                  <td style={loginLogTd}>{(l.login_time || '').slice(11, 16) || '-'}</td>
                  <td style={loginLogTd}>{l.logout_time ? l.logout_time.slice(11, 16) : '-'}</td>
                  <td style={loginLogTd}>{l.ip || '-'}</td>
                  <td style={loginLogTd}>
                    <span style={{ padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                      background: l.location === '사내' ? '#dbeafe' : '#fef3c7',
                      color: l.location === '사내' ? '#1d4ed8' : '#b45309' }}>
                      {l.location || '-'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
const loginLogTh = { padding: '8px 6px', border: '1px solid #eee', fontWeight: 600, color: '#555' };
const loginLogTd = { padding: '7px 6px', border: '1px solid #f0f0f0', textAlign: 'center' };

// ── 비정상접속 경고 모달(2026-09 환경설정 > 로그기록 4번 요청) ────────────────────
// "비정상 로그인 및 로그시도가 발생한 경우 관리자모드로 접속시 경고 모달창을 띄움 —
// 접속아이피/아이피공급자/지역/시간/시도횟수 포함." GET /admin/security-logs/alerts가
// IP별로 묶어서 내려주는 값을 그대로 표로 보여준다. 닫기를 누르면 상위(MyPage)의
// closeSecurityAlerts가 각 IP를 서버에 ack 처리해 다음 관리자모드 진입 때 반복 표시되지
// 않게 한다.
function SecurityAlertModal({ alerts, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 620, maxWidth: '92vw', maxHeight: '86vh',
        overflowY: 'auto', border: '2px solid #dc2626' }}>
        <div style={{ padding: '18px 22px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#dc2626' }}>
            ⚠️ 비정상접속이 감지되었습니다
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#999', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '16px 22px 22px' }}>
          <p style={{ fontSize: 13, color: '#666', margin: '0 0 14px' }}>
            아래 접속(IP)에서 비정상적인 로그인 시도 또는 미인증 접근이 반복 발생했습니다. 확인 후 필요시 조치해주세요.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {alerts.map(a => (
              <div key={a.ip} style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>
                  📍 {a.ip} <span style={{ fontSize: 11, fontWeight: 600, color: a.location === '사내' ? '#1d4ed8' : '#b45309' }}>
                    ({a.location || '-'})
                  </span>
                </div>
                <table style={{ width: '100%', fontSize: 12.5, color: '#444' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '2px 8px 2px 0', color: '#888', width: 90 }}>아이피공급자</td>
                      <td>{a.isp || '알수없음'}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '2px 8px 2px 0', color: '#888' }}>지역</td>
                      <td>{a.region || '알수없음'}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '2px 8px 2px 0', color: '#888' }}>발생시간</td>
                      <td>{a.first_time || '-'} ~ {a.last_time || '-'}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '2px 8px 2px 0', color: '#888' }}>시도횟수</td>
                      <td style={{ fontWeight: 700 }}>{a.attempt_count}회</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '2px 8px 2px 0', color: '#888' }}>사유</td>
                      <td>{a.reason || '-'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <button onClick={onClose} style={{ marginTop: 16, width: '100%', padding: '10px 0', borderRadius: 8,
            border: 'none', background: '#dc2626', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            확인했습니다
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 근로계약서 서명하기 모달(문서 미리보기 + 서명위치 오버레이) ────────────────────
// 요청사항: "근로자가 서명하기를 누르면 미리보는 화면이 나타나고 서명 위치에 서명하기
// 버튼을 추가생성하여 해당 버튼을 누르면 이미 등록된 서명을 실시간으로 적용하여 화면에
// 보여줌" — 예전에는 문서를 보여주지 않고 "등록된 서명으로 서명하기" 버튼 하나만 있는
// 확인창이었다. 이제 실제 계약서 문서를 모달 안에 띄우고, 서명란
// ("(근로자)  이름  (인 또는 서명)")이 있는 위치에 맞춰 서명 버튼을 오버레이로 얹는다.
// 이 좌표는 근로계약서 템플릿(backend/templates/LaborContract_Form.docx)이 항상 동일한
// 고정 서식(3페이지, 마지막 페이지 하단 서명란)이라 매번 같은 상대위치에 나타남을
// 실제로 렌더링해 확인한 값이다(문서 폭 기준 좌측 65%·상단 76% 지점, 폭 21%·높이 13%
// 크기). 브라우저 PDF 뷰어의 확대/축소 설정에 따라 소폭 어긋날 수 있다.
// 버튼을 누르면 계정관리에 등록해둔 서명을 그대로 적용해 서버가 계약서에 합성하고,
// 반영이 끝나면 미리보기를 다시 불러와(blob 재요청) 그 자리에 실제 서명이 찍힌 최신
// 문서를 바로 보여준다("실시간 반영"). 이 계약서 전용으로 별도 PNG를 첨부하는 기존
// 방식도 접이식 옵션으로 그대로 유지한다.
function LaborContractSignModal({ item, onClose, onSigned, hasProfileSignature }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSigned, setJustSigned] = useState(item.signature_status === 'signed');
  const [reloadTick, setReloadTick] = useState(0);
  const [showFileUpload, setShowFileUpload] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objUrl;
    setBlobUrl(null); setLoadFailed(false);
    // 2026-08 추가: 서명 직후(reloadTick 증가로 인한 재조회) 서버가 방금 막 새로 덮어쓴 PDF를
    // 곧바로 다시 읽으려 하면, 백신 프로그램의 신규파일 검사 잠금이나 디스크 flush 지연 등으로
    // 아주 짧은 순간 파일을 읽지 못해 실패할 수 있다(사용자 재보고: 서명하기를 누르면
    // "문서를 불러오지 못했습니다" 화면이 뜸). 즉시 실패로 단정하지 않고 짧은 간격을 두고
    // 최대 3회까지 재시도한 뒤에만 최종 실패로 표시한다.
    const fetchWithRetry = (attempt) => {
      API.get(`/hr/file/${encodeURIComponent(item.filename)}`, { responseType: 'blob' })
        .then(r => {
          if (cancelled) return;
          setMimeType(r.data.type);
          objUrl = URL.createObjectURL(r.data);
          setBlobUrl(objUrl);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 3) setTimeout(() => { if (!cancelled) fetchWithRetry(attempt + 1); }, 1000 * attempt);
          else setLoadFailed(true);
        });
    };
    fetchWithRetry(1);
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.filename, reloadTick]);

  const isPdf = mimeType === 'application/pdf';

  const applySignature = (file) => {
    setSubmitting(true);
    let req;
    if (file) {
      const fd = new FormData();
      fd.append('file', file);
      req = API.post(`/mypage/labor-contract/${item.id}/sign`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      req = API.post(`/mypage/labor-contract/${item.id}/sign`, { useProfileSignature: true });
    }
    req
      .then(() => {
        setJustSigned(true);
        setShowFileUpload(false);
        setReloadTick(t => t + 1); // 서명이 합성된 최신 문서로 미리보기를 즉시 다시 불러온다
        if (onSigned) onSigned();
      })
      .catch(err => alert(err.response?.data?.message || '서명 처리 중 오류가 발생했습니다.'))
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal title={`✍️ 근로계약서 서명 — ${item.contract_year}년 ${item.contract_month}월 ${item.contract_day}일`}
      onClose={onClose} width={860}>
      <div style={{ position: 'relative', border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', background: '#f8fafc' }}>
        {blobUrl && isPdf && (
          <iframe src={blobUrl + '#page=3'} title="근로계약서 미리보기"
            style={{ width: '100%', height: 560, border: 'none', display: 'block' }} />
        )}
        {blobUrl && !isPdf && (
          <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 13 }}>
            이 형식은 미리보기를 지원하지 않습니다. 아래 버튼으로 바로 서명해주세요.
          </div>
        )}
        {!blobUrl && !loadFailed && (
          <div style={{ padding: 60, textAlign: 'center', color: '#999', fontSize: 13 }}>불러오는 중...</div>
        )}
        {loadFailed && (
          <div style={{ padding: 60, textAlign: 'center', color: '#e05a5a', fontSize: 13 }}>
            문서를 불러오지 못했습니다.
            <div style={{ marginTop: 10 }}>
              <button onClick={() => setReloadTick(t => t + 1)}
                style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>
                다시 시도
              </button>
            </div>
          </div>
        )}

        {blobUrl && isPdf && !justSigned && (
          <button onClick={() => applySignature(null)} disabled={submitting || !hasProfileSignature}
            title={hasProfileSignature ? '등록된 서명을 이 위치에 적용합니다' : '계정관리에 서명을 먼저 등록해주세요'}
            style={{ position: 'absolute', left: '65%', top: '76%', width: '21%', height: '13%',
              background: hasProfileSignature ? 'rgba(24,95,165,0.85)' : 'rgba(170,170,170,0.7)',
              color: '#fff', border: '2px dashed #fff', borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: hasProfileSignature ? 'pointer' : 'not-allowed',
              opacity: submitting ? 0.6 : 1 }}>
            {submitting ? '적용 중...' : '✍️ 서명하기'}
          </button>
        )}
        {justSigned && blobUrl && isPdf && (
          <div style={{ position: 'absolute', left: '65%', top: '76%', width: '21%', height: '13%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(5,150,105,0.12)', border: '2px solid #059669', borderRadius: 6 }}>
            <span style={{ color: '#059669', fontWeight: 700, fontSize: 12 }}>✅ 서명완료</span>
          </div>
        )}
      </div>

      {!hasProfileSignature && !justSigned && (
        <div style={{ fontSize: 11, color: '#e05a5a', marginTop: 8 }}>
          계정관리에 등록된 서명이 없습니다. 먼저 "서명등록"에서 서명 이미지를 등록해주세요.
        </div>
      )}
      {justSigned && (
        <div style={{ fontSize: 11, color: '#059669', marginTop: 8 }}>
          서명이 완료되어 계약서에 반영되었습니다. 위 미리보기에 실제 서명이 찍힌 최신 문서가 표시됩니다.
        </div>
      )}
      {!justSigned && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowFileUpload(v => !v)}
            style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: 11, cursor: 'pointer', padding: 0 }}>
            {showFileUpload ? '접기' : '이 계약서 전용으로 다른 서명 이미지 첨부하기'}
          </button>
          {showFileUpload && (
            <div style={{ marginTop: 8 }}>
              <FileDropZone onFile={(file) => applySignature(file)} accept="image/png,.png"
                fileName={submitting ? '업로드 중...' : undefined} />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── 근로서약서 / 개인정보 제공 동의서 서명하기 모달 ────────────────────────────
// 2026-08 요청: "근로서약서, 개인정보동의서 서명을 누르면 근로계약서와 같이 화면이 보이고
// 서명하기 버튼이 화면에 생성됨" — 예전에는 문서를 보여주지 않고 "등록된 서명으로
// 서명하기" 버튼 하나만 있는 확인창이었다. LaborContractSignModal과 완전히 동일한 방식
// (문서 미리보기 + 서명란 위치에 오버레이 버튼)으로 개편한다.
// 오버레이 좌표는 각 템플릿(Pledge_Form.docx/PrivacyConsent_Form.docx)을 실제로 렌더링해
// 서명 이미지가 나타나는 위치를 픽셀 단위로 측정해 확인한 값이다. 근로계약서 템플릿과
// 달리 이 두 문서는 서명 이미지가 텍스트 위에 겹쳐지지 않고(docxtemplater-image-module-free
// 기본 동작인 인라인 이미지 그대로 사용) 서명란 문구 바로 아래 새 줄에 나타나므로, 오버레이
// 버튼도 그 자리(문서 폭 기준 좌측 12%, 폭 14%)에 맞춘다. 근로서약서는 1페이지 문서라 항상
// 1페이지, 개인정보동의서는 항상 3페이지 문서라 마지막 페이지(3페이지) 하단에 서명란이 있다.
// 2026-08 수정: 근로서약서 템플릿(Pledge_Form.docx)에서 서명 이미지가 들어가는 문단에 좌측
// 고정 들여쓰기(twips)를 추가해 "(인 또는 서명)" 문구 바로 아래에 찍히도록 했으나(요청사항:
// "서명하기 버튼 위치를 화면상 (인 또는 서명) 바로 아래 위치로 이동"), 실제 서버(윈도우)에서
// 렌더링해보니 이름 글자 수/폰트에 따라 "(인 또는 서명)" 문구의 가로 위치가 달라져(짧은
// 이름일수록 라벨이 왼쪽으로 당겨짐) 고정 들여쓰기 값과 어긋나 서명이 문구와 겹치는 버그가
// 있었다(사용자 재보고). 그래서 절대 들여쓰기 대신 <w:jc w:val="right"/>(문단 우측정렬)로
// 바꿔, 서명 이미지가 항상 페이지 우측 여백에 고정되도록 수정했다 — 이름 길이나 폰트와 무관하게
// 항상 같은 자리에 찍히므로 근본적으로 겹칠 수 없다. 세로 간격도 약간의 여유(6pt)를 추가했다.
// 2026-08 추가 수정: 개인정보 제공 동의서(PrivacyConsent_Form.docx)도 동일한 문제였다 —
// "성 명 [이름] (서명)" 한 줄에서 이름이 길면 "(서명)"이 다음 줄로 밀려나는 등 라벨 위치가
// 이름 길이에 따라 크게 흔들렸는데, 서명 이미지는 여전히 페이지 좌측(줄바꿈 전 원래 위치)에
// 고정되어 있어 사용자가 "서명하기 버튼이 (서명) 바로 아래가 아니다"라고 재보고했다. 근로서약서와
// 동일하게 우측정렬로 바꿔 문제를 해결했다 — 라벨이 같은 줄에 있든 다음 줄로 밀리든, 그 바로
// 다음 문단(서명 이미지)은 항상 우측 여백에 나타나 라벨과 겹치지 않는다(테스트: 이름 4자/6자
// 두 경우 모두 서명 이미지 픽셀 위치가 완전히 동일함을 확인).
// 2026-08 수정: "세번째 서명위치와 같이 (인 또는 서명) 글자위에 서명이 위치하도록 수정" —
// 근로계약서처럼 서명 이미지가 라벨 글자 위에 겹치는 플로팅 방식(hrExtraDocx.js의
// floatSignatureImage)으로 바뀌면서 실제 서명이 그려지는 위치도 이동했다. 아래 좌표는
// 테스트 서명 이미지를 실제로 렌더링한 뒤 pdftoppm+PIL로 서명 이미지의 픽셀 좌표를
// 측정해(문서 크기 595.3x841.9pt 기준 백분율로 환산) 서명 버튼이 실제 서명이 찍히는
// 위치와 겹치도록 다시 계산한 값이다(이전: 라벨 "아래"에 깔끔히 위치 → 지금: 라벨
// "위"에 겹쳐서 도장처럼 위치).
// 2026-08 추가: "근로서약서, 개인정보동의서 서명하기 버튼 스타일을 근로계약서 스타일로
// 통일" — overlay(위 서명 이미지가 실제로 찍히는 정확한 위치/크기, 픽셀 측정으로 튜닝된
// 값)는 서명완료 후 도장처럼 겹쳐 보여야 하므로 그대로 두고, 서명 "전" 클릭 버튼만 근로계약서
// 버튼과 비슷한 크기(가로/세로가 더 큼직한 박스)로 보이도록 buttonOverlay를 별도로 둔다.
// (중심 좌표는 원래 overlay와 동일하게 유지하고 크기만 키운 값)
const ESIGN_DOC_CONFIG = {
  pledge: {
    label: '근로서약서', page: 1,
    overlay: { left: '70%', top: '78%', width: '16%', height: '5.5%' },
    buttonOverlay: { left: '68%', top: '75%', width: '20%', height: '12%' },
  },
  privacy_consent: {
    label: '개인정보 제공 동의서', page: 3,
    overlay: { left: '81%', top: '70%', width: '16%', height: '5.5%' },
    buttonOverlay: { left: '80%', top: '67%', width: '18%', height: '12%' },
  },
};

// 2026-08 추가 — 개인정보 제공 동의서 안에는 4개 항목(개인정보/고유식별정보/민감정보/제3자 제공)이
// 있고 각 항목마다 "( □동의함 □동의하지 않음 )" 체크박스가 있다. 근로자 본인이 서명 전에 4개
// 항목 각각을 실제로 선택하게 하려고 목록으로 둔다(순서는 서버 fillPrivacyConsent의 knownData
// 동의1a~4b 순서와 반드시 일치해야 함).
const PRIVACY_CONSENT_ITEMS = [
  '1. 개인정보의 수집·이용',
  '2. 고유식별정보의 수집·이용',
  '3. 민감정보의 수집·이용',
  '4. 개인정보의 제3자 제공',
];

function EsignDocSignModal({ item, onClose, onSigned, hasProfileSignature }) {
  const config = ESIGN_DOC_CONFIG[item.doc_type] || ESIGN_DOC_CONFIG.pledge;
  const isPrivacyConsent = item.doc_type === 'privacy_consent';
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSigned, setJustSigned] = useState(item.status === '서명완료');
  const [reloadTick, setReloadTick] = useState(0);
  const [showFileUpload, setShowFileUpload] = useState(false);
  // 4개 항목 선택값 — 이미 서명완료된 문서를 다시 열었으면 서버가 내려준 값을 그대로 복원해
  // 보여준다(읽기 전용 확인용). 아직 서명 전이면 전부 미선택(null) 상태로 시작한다.
  const [consentChoices, setConsentChoices] = useState(() => {
    if (!isPrivacyConsent) return [];
    try {
      const parsed = item.consent_choices ? JSON.parse(item.consent_choices) : null;
      if (Array.isArray(parsed) && parsed.length === 4) return parsed;
    } catch (e) { /* ignore */ }
    return [null, null, null, null];
  });
  const allConsentsSelected = !isPrivacyConsent ||
    (consentChoices.length === 4 && consentChoices.every(c => c === 'agree' || c === 'disagree'));

  useEffect(() => {
    let cancelled = false;
    let objUrl;
    setBlobUrl(null); setLoadFailed(false);
    // 2026-08 추가: 서명 직후(reloadTick 증가로 인한 재조회) 서버가 방금 막 새로 덮어쓴 PDF를
    // 곧바로 다시 읽으려 하면, 백신 프로그램의 신규파일 검사 잠금이나 디스크 flush 지연 등으로
    // 아주 짧은 순간 파일을 읽지 못해 실패할 수 있다(사용자 재보고: 서명하기를 누르면
    // "문서를 불러오지 못했습니다" 화면이 뜸). 즉시 실패로 단정하지 않고 짧은 간격을 두고
    // 최대 3회까지 재시도한 뒤에만 최종 실패로 표시한다.
    const fetchWithRetry = (attempt) => {
      API.get(`/hr/file/${encodeURIComponent(item.filename)}`, { responseType: 'blob' })
        .then(r => {
          if (cancelled) return;
          setMimeType(r.data.type);
          objUrl = URL.createObjectURL(r.data);
          setBlobUrl(objUrl);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 3) setTimeout(() => { if (!cancelled) fetchWithRetry(attempt + 1); }, 1000 * attempt);
          else setLoadFailed(true);
        });
    };
    fetchWithRetry(1);
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.filename, reloadTick]);

  const isPdf = mimeType === 'application/pdf';

  const applySignature = (file) => {
    // 개인정보동의서는 4개 항목 모두 동의함/동의하지 않음을 선택해야만 서명을 진행한다
    // (서명 버튼이 비활성화되어 있어도, 방어적으로 여기서도 한 번 더 막는다).
    if (isPrivacyConsent && !allConsentsSelected) {
      alert('4개 항목 모두 동의함 / 동의하지 않음을 선택해주세요.');
      return;
    }
    setSubmitting(true);
    let req;
    if (file) {
      const fd = new FormData();
      fd.append('file', file);
      if (isPrivacyConsent) fd.append('consentChoices', JSON.stringify(consentChoices));
      req = API.post(`/mypage/esign-docs/${item.id}/sign`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      req = API.post(`/mypage/esign-docs/${item.id}/sign`, {
        useProfileSignature: true,
        ...(isPrivacyConsent ? { consentChoices: JSON.stringify(consentChoices) } : {}),
      });
    }
    req
      .then(() => {
        setJustSigned(true);
        setShowFileUpload(false);
        setReloadTick(t => t + 1); // 서명이 합성된 최신 문서로 미리보기를 즉시 다시 불러온다
        if (onSigned) onSigned();
      })
      .catch(err => alert(err.response?.data?.message || '서명 처리 중 오류가 발생했습니다.'))
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal title={`✍️ ${config.label} 전자서명`} onClose={onClose} width={860}>
      <div style={{ position: 'relative', border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', background: '#f8fafc' }}>
        {blobUrl && isPdf && (
          <iframe src={blobUrl + `#page=${config.page}`} title={`${config.label} 미리보기`}
            style={{ width: '100%', height: 560, border: 'none', display: 'block' }} />
        )}
        {blobUrl && !isPdf && (
          <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 13 }}>
            이 형식은 미리보기를 지원하지 않습니다. 아래 버튼으로 바로 서명해주세요.
          </div>
        )}
        {!blobUrl && !loadFailed && (
          <div style={{ padding: 60, textAlign: 'center', color: '#999', fontSize: 13 }}>불러오는 중...</div>
        )}
        {loadFailed && (
          <div style={{ padding: 60, textAlign: 'center', color: '#e05a5a', fontSize: 13 }}>
            문서를 불러오지 못했습니다.
            <div style={{ marginTop: 10 }}>
              <button onClick={() => setReloadTick(t => t + 1)}
                style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>
                다시 시도
              </button>
            </div>
          </div>
        )}

        {blobUrl && isPdf && !justSigned && (
          <button onClick={() => applySignature(null)} disabled={submitting || !hasProfileSignature || !allConsentsSelected}
            title={!allConsentsSelected ? '아래 4개 항목의 동의 여부를 먼저 선택해주세요'
              : (hasProfileSignature ? '등록된 서명을 이 위치에 적용합니다' : '계정관리에 서명을 먼저 등록해주세요')}
            style={{ position: 'absolute', ...config.buttonOverlay,
              background: (hasProfileSignature && allConsentsSelected) ? 'rgba(24,95,165,0.85)' : 'rgba(170,170,170,0.7)',
              color: '#fff', border: '2px dashed #fff', borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: (hasProfileSignature && allConsentsSelected) ? 'pointer' : 'not-allowed',
              opacity: submitting ? 0.6 : 1 }}>
            {submitting ? '적용 중...' : '✍️ 서명하기'}
          </button>
        )}
        {justSigned && blobUrl && isPdf && (
          <div style={{ position: 'absolute', ...config.overlay,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(5,150,105,0.12)', border: '2px solid #059669', borderRadius: 6 }}>
            <span style={{ color: '#059669', fontWeight: 700, fontSize: 12 }}>✅ 서명완료</span>
          </div>
        )}
      </div>

      {isPrivacyConsent && (
        <div style={{ marginTop: 10, border: '1px solid #eee', borderRadius: 8, padding: '10px 12px', background: '#fafafa' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 8 }}>
            항목별 개인정보 수집·이용 동의 여부{!justSigned ? ' (서명 전 필수 선택)' : ''}
          </div>
          {PRIVACY_CONSENT_ITEMS.map((label, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderTop: idx > 0 ? '1px solid #eee' : 'none' }}>
              <span style={{ fontSize: 12, color: '#555' }}>{label}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" disabled={justSigned}
                  onClick={() => setConsentChoices(cs => cs.map((c, i) => i === idx ? 'agree' : c))}
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5,
                    border: '1px solid ' + (consentChoices[idx] === 'agree' ? '#185FA5' : '#ccc'),
                    background: consentChoices[idx] === 'agree' ? '#185FA5' : '#fff',
                    color: consentChoices[idx] === 'agree' ? '#fff' : '#555',
                    cursor: justSigned ? 'default' : 'pointer' }}>
                  동의함
                </button>
                <button type="button" disabled={justSigned}
                  onClick={() => setConsentChoices(cs => cs.map((c, i) => i === idx ? 'disagree' : c))}
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5,
                    border: '1px solid ' + (consentChoices[idx] === 'disagree' ? '#e05a5a' : '#ccc'),
                    background: consentChoices[idx] === 'disagree' ? '#e05a5a' : '#fff',
                    color: consentChoices[idx] === 'disagree' ? '#fff' : '#555',
                    cursor: justSigned ? 'default' : 'pointer' }}>
                  동의하지 않음
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!hasProfileSignature && !justSigned && (
        <div style={{ fontSize: 11, color: '#e05a5a', marginTop: 8 }}>
          계정관리에 등록된 서명이 없습니다. 먼저 "서명등록"에서 서명 이미지를 등록해주세요.
        </div>
      )}
      {justSigned && (
        <div style={{ fontSize: 11, color: '#059669', marginTop: 8 }}>
          서명이 완료되어 문서에 반영되었습니다. 위 미리보기에 실제 서명이 찍힌 최신 문서가 표시됩니다.
        </div>
      )}
      {!justSigned && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowFileUpload(v => !v)} disabled={!allConsentsSelected}
            style={{ background: 'none', border: 'none', color: allConsentsSelected ? '#185FA5' : '#bbb',
              fontSize: 11, cursor: allConsentsSelected ? 'pointer' : 'not-allowed', padding: 0 }}>
            {showFileUpload ? '접기' : '이 문서 전용으로 다른 서명 이미지 첨부하기'}
          </button>
          {showFileUpload && (
            <div style={{ marginTop: 8 }}>
              <FileDropZone onFile={(file) => applySignature(file)} accept="image/png,.png"
                fileName={submitting ? '업로드 중...' : undefined} />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function MyPage() {
  const { user, logout } = useAuth();
  // 2026-09 요청: "정보수정을 관리자 권한을 가질 경우 수정요청 없이 바로 수정" — 최고관리자
  // 또는 부여받은 관리자는 승인 절차 없이 즉시 반영되므로, 안내 문구와 버튼 동작을 다르게
  // 보여준다(실제 즉시반영 로직은 백엔드 POST /mypage/request-edit에서 처리).
  const isAdminUser = user?.role === 'superadmin' || user?.role === 'admin';
  // role==='admin'(관리자 권한을 부여받은 계정)도 최고관리자와 동일한 화면/기능 접근 권한을
  // 가지므로(2026-08 추가) isSuperAdmin에는 admin도 함께 포함한다. 다만 "관리자 권한 부여/회수"
  // 기능 자체와 제목 옆 "최고관리자" 배지는 진짜 최고관리자(ninegro, role==='superadmin')에게만
  // 보여야 하므로 별도로 isTrueSuperAdmin(리터럴 비교)을 둔다.
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const isTrueSuperAdmin = user?.role === 'superadmin';
  // 2026-09 요청: "관리자 권한이 부여되더라도 기존 계정정보(현재 내 정보/근태현황/연차사용현황)는
  // 그대로 있어야 한다" — 관리자 권한을 부여받은 계정(role==='admin')이라도 이 화면을 열면 항상
  // 회원 리스트(관리 화면)로 바뀌어버리면 본인 계정정보를 확인할 방법이 없어진다. 그래서 화면을
  // "관리자모드"(회원 리스트로 각 계정 관리) / "사용자모드"(본인 계정정보+근태+연차, 기존 화면)
  // 두 가지로 나누고 헤더의 토글 버튼으로 전환한다. 진짜 최고관리자(ninegro)는 직원이 아니라
  // 애초에 확인할 계정정보가 없으므로 기본값을 관리자모드로 두고, 관리자 권한을 부여받은 계정은
  // 기존처럼 본인 정보가 먼저 보이도록 기본값을 사용자모드로 둔다.
  const [adminViewMode, setAdminViewMode] = useState(() => (isTrueSuperAdmin ? 'admin' : 'user'));
  const showAdminCard = isSuperAdmin && adminViewMode === 'admin';

  // 관리자모드로 들어올 때마다(최초 진입 포함) 확인하지 않은 비정상접속이 있는지 조회한다.
  useEffect(() => {
    if (!showAdminCard) return;
    API.get('/admin/security-logs/alerts')
      .then(r => setSecurityAlerts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [showAdminCard]);

  const closeSecurityAlerts = () => {
    // 지금 보여준 항목들을 각각 ack 처리 — 실패해도 화면은 그냥 닫는다(다음 관리자모드 진입 때
    // 다시 물어보는 정도의 영향만 있고 치명적이지 않음).
    securityAlerts.forEach(a => {
      API.post('/admin/security-logs/alerts/ack', { ip: a.ip, max_log_id: a.max_log_id }).catch(() => {});
    });
    setSecurityAlerts([]);
  };
  const perm = usePermissions();
  const [info, setInfo]     = useState(null);
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [requests, setRequests]   = useState([]);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  // 2026-08 후속 요청: "전용계정 관리" 버튼 자리에 비밀번호 변경 버튼을 두고, 클릭하면 모달창으로
  // 연다(예전에는 화면 맨 아래 항상 펼쳐진 카드였다).
  const [showPwModal, setShowPwModal] = useState(false);
  // 2026-09 신규 — "최고관리자/관리자로 접속할 경우 계정관리 우측 상단에 회사정보 버튼을
  // 추가"(회사정보는 견적서/거래명세서 등에서 공급자 정보의 단일 출처로 쓰인다).
  // 회원 목록 컬럼헤더 클릭 정렬(2026-08 추가) — 화면 진입시 기본 정렬 기준은 "직급 순서".
  const [memberSortKey, setMemberSortKey] = useState('position');
  const [memberSortDir, setMemberSortDir] = useState('asc');
  const [msg, setMsg]       = useState('');
  const [msgType, setMsgType] = useState('success');
  const [loading, setLoading] = useState(false);
  // 인사관리 > 휴가관리에 있던 "내 연차현황" 카드를 계정관리(마이페이지)로 이동한 것 — 데이터
  // 소스(GET /hr/leaves/balance)는 그대로이며, 화면 위치만 헤더 우측(마이페이지 제목 옆)으로
  // 옮겼다. 인사관리 대상(사내임직원)이 아닌 계정은 404/403이 날 수 있어 실패 시 조용히 숨긴다.
  const [leaveBalance, setLeaveBalance] = useState(null);

  // "정보수정"(핵심 계정정보 수정요청) 모달 — 예전엔 페이지 하단에 별도 카드로 항상 노출됐던
  // 기능을 모달로 감쌌다. 로직(관리자 승인이 필요한 수정요청)은 그대로다.
  const [showInfoEditModal, setShowInfoEditModal] = useState(false);

  // "추가정보"(인사기록카드) 모달 — 인사관리 > 직원등록과 완전히 동일한 입력폼(HrCardFields)을
  // 그대로 재사용해 본인 정보를 직접 조회/저장한다. 관리자 승인 없이 본인이 바로 반영할 수
  // 있는 부가정보(가족/학력/경력/자격/건강/병역 등)만 다룬다.
  const [showHrCardModal, setShowHrCardModal] = useState(false);
  const [hrCardForm, setHrCardForm] = useState(null);
  const [hrCardLoading, setHrCardLoading] = useState(false);
  const [hrCardSaving, setHrCardSaving] = useState(false);

  // "서명등록"(전자결재 결재/승인 화면에서 도장처럼 쓰이는 PNG 서명 이미지) 모달 —
  // 정보수정 버튼 왼쪽에 배치. PNG 파일만 등록 가능하며, 등록/교체/삭제 모두 즉시 반영된다.
  const [showSigModal, setShowSigModal] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);

  // "관리자 권한부여" 자리 → 회원 리스트로 개편(2026-08). 승인된 전체 회원을 표로 보여주고
  // 부서/직급/회원구분 변경, 관리자 권한 부여/회수, 비밀번호 초기화, 권한설정을 각 행에서
  // 바로 처리한다(예전 인사관리 > 사원정보의 "계정관리"+"권한설정" 버튼 기능을 이 목록으로
  // 옮겨왔다 — 자세한 배경은 위 MemberRow 정의부 주석 참고).
  const [memberList, setMemberList] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDepartments, setMemberDepartments] = useState([]);
  const [memberPositions, setMemberPositions] = useState([]);
  const [grantingId, setGrantingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [savingFieldId, setSavingFieldId] = useState(null); // "<userId>-type|dept|pos" 저장 중 표시
  const [permMenus, setPermMenus] = useState([]);
  const [permRows, setPermRows] = useState([]);
  const [permDetailRows, setPermDetailRows] = useState([]);
  const [permUser, setPermUser] = useState(null);
  // 2026-09 요청: 계정관리(관리자모드) 목록의 "📡 접속로그" 버튼 — 클릭한 회원의 로그인
  // 일자/시간/IP(사내·사외)를 모달로 보여준다. 열려있는 대상 회원(null이면 닫힘)만 들고 있으면
  // 되고, 실제 로그 데이터/년월 필터는 LoginLogModal 내부 상태로 따로 관리한다(모달 열 때마다
  // 새로 불러오므로 여기서 캐시할 필요가 없다).
  const [loginLogUser, setLoginLogUser] = useState(null);
  // 2026-09 요청(환경설정 > 로그기록 4번): "비정상 로그인 및 로그시도가 발생한 경우 관리자모드로
  // 접속시 경고 모달창을 띄움(접속아이피/아이피공급자/지역/시간/시도횟수)". 관리자모드로 전환될
  // 때마다(showAdminCard가 true가 될 때) 서버에 아직 확인하지 않은 비정상접속이 있는지
  // 물어보고, 있으면 SecurityAlertModal로 보여준다. 확인(닫기) 시 서버에 ack를 보내 같은
  // 클러스터를 반복해서 띄우지 않는다(아래 useEffect + closeSecurityAlerts 참고).
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [detailUser, setDetailUser] = useState(null);
  const [detailMenuKey, setDetailMenuKey] = useState(null);
  const [detailLiveOverride, setDetailLiveOverride] = useState(null);
  // 2026-08 후속 요청 2·3: 회원 정보수정 요청 승인/거부, 신규가입 승인/거부도 이 목록 화면에서
  // 함께 처리한다(둘 다 기존에 존재하던 API를 그대로 재사용 — AdminUsers.js/HRManagement.js와 동일).
  const [editRequests, setEditRequests] = useState([]);
  const [editReqActingId, setEditReqActingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);

  const loadMemberList = () => {
    API.get('/admin/users').then(r => setMemberList(Array.isArray(r.data) ? r.data : [])).catch(() => setMemberList([]));
  };
  const loadMemberOrg = () => API.get('/org').then(r => {
    setMemberDepartments(r.data.departments || []);
    setMemberPositions(r.data.positions || []);
  }).catch(() => {});
  const loadMemberPerms = () => API.get('/permissions').then(r => {
    setPermMenus(r.data.menus || []); setPermRows(r.data.permissions || []); setPermDetailRows(r.data.details || []);
  }).catch(() => {});
  const loadEditRequests = () => {
    API.get('/admin/edit-requests').then(r => setEditRequests(Array.isArray(r.data) ? r.data : [])).catch(() => setEditRequests([]));
  };

  const showMsg = (text, type = 'success') => { setMsg(text); setMsgType(type); setTimeout(() => setMsg(''), 4000); };

  // 내 정보 불러오기
  // (예전에는 관리자 전용 API를 썼는데, 관리자가 아닌 계정은 403을 받고도 아무 처리를
  // 안 해서 "불러오는 중..."이 계속 떠 있었음 — 본인 조회 전용 API로 교체)
  const loadInfo = () => {
    API.get('/mypage/me').then(r => setInfo(r.data)).catch(() => {});
  };

  // 내 수정 요청 목록
  const loadRequests = () => {
    API.get('/mypage/requests').then(r => setRequests(r.data)).catch(() => {});
  };

  const loadLeaveBalance = () => {
    API.get('/hr/leaves/balance').then(r => setLeaveBalance(r.data)).catch(() => setLeaveBalance(null));
  };

  // 근태현황/연차사용현황 — 요청사항: "현재 내 정보 아래쪽에 근태현황과 연차사용현황을 확인
  // 할 수 있는 부분을 추가". 근태는 최근 1개월 출퇴근 기록, 연차는 올해 사용 신청 내역을
  // 각각 본인 전용 라우트(mypage.js의 GET /attendance, GET /leaves)로 조회한다.
  const [attendanceList, setAttendanceList] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const loadAttendanceList = () => {
    setLoadingAttendance(true);
    API.get('/mypage/attendance', { params: { months: 1 } })
      .then(r => setAttendanceList(r.data.list || []))
      .catch(() => setAttendanceList([]))
      .finally(() => setLoadingAttendance(false));
  };

  const [leavesList, setLeavesList] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const loadLeavesList = () => {
    setLoadingLeaves(true);
    API.get('/mypage/leaves', { params: { year: new Date().getFullYear() } })
      .then(r => setLeavesList(r.data.list || []))
      .catch(() => setLeavesList([]))
      .finally(() => setLoadingLeaves(false));
  };

  const ATT_STATUS_LABEL = { 정상: '정상', 지각: '지각', 결근: '결근', 휴가: '휴가', 외근: '외근', 출장: '출장' };
  const LEAVE_STATUS_COLOR = { 대기: '#faad14', 승인: '#185FA5', 거부: '#d33' };

  // 근로계약서(전자) — 인사관리에서 관리자가 "계약서 생성 및 인쇄"를 누르면 여기에
  // "발급대기중"으로 뜬다. 본인이 "확인 및 출력"을 눌러 실제로 열어봐야 발급이력에 기록된다
  // (요청사항: "발급이력은 계약상대인 근로자가 출력한 경우 기록").
  const [laborContracts, setLaborContracts] = useState([]);
  const [loadingLaborContracts, setLoadingLaborContracts] = useState(false);
  const [printingContractId, setPrintingContractId] = useState(null);

  const loadLaborContracts = () => {
    setLoadingLaborContracts(true);
    API.get('/mypage/labor-contract')
      .then(r => setLaborContracts(r.data || []))
      .catch(() => setLaborContracts([]))
      .finally(() => setLoadingLaborContracts(false));
  };

  // 근로계약(스캔) — 인사관리에서 원문 그대로 업로드해둔 근로계약서를 본인이 계정관리에서
  // 확인/출력할 수 있게 한다(요청사항 2.2). 전자근로계약과 달리 파일 하나만 존재하며,
  // 출력할 때마다 hr_labor_contract_scan_logs에 한 줄씩 기록된다(인사기록카드-근로계약(스캔)
  // 에서 관리자가 그 출력이력을 본다).
  const [scanContractFilename, setScanContractFilename] = useState(null);
  const [printingScan, setPrintingScan] = useState(false);
  const loadScanContract = () => {
    API.get('/mypage/labor-contract-scan')
      .then(r => setScanContractFilename((r.data && r.data.filename) || null))
      .catch(() => setScanContractFilename(null));
  };
  // 요청사항: "미리보기 없이 다운로드 버튼이 있는데 미리보기가 가능하게 수정" — 지금까지는
  // window.open(blob URL)로 새 브라우저 탭을 띄웠는데, PDF 뷰어가 없는 환경에서는 그대로
  // 다운로드로 동작해버렸다. FilePreviewModal(모달 안 iframe)로 열어 항상 미리보기가 보장되게 한다.
  const [previewModal, setPreviewModal] = useState(null); // { title, filename } | null
  const printScanContract = () => {
    if (!scanContractFilename) return;
    setPrintingScan(true);
    API.post('/mypage/labor-contract-scan/print')
      .then(() => setPreviewModal({ title: '근로계약서(원문)', filename: scanContractFilename }))
      .catch(() => showMsg('근로계약서(원문)를 불러오지 못했습니다.', 'error'))
      .finally(() => setPrintingScan(false));
  };

  // 요청사항: "계정관리-서명등록 왼쪽에 근로계약 버튼을 자동으로 생성" — 전자근로계약이
  // 하나라도 있거나(발급대기중/서명대기/서명완료 등 상태 무관) 근로계약(스캔) 원문이
  // 등록되어 있으면 버튼을 노출한다. 눌러서 여는 통합 모달 안에서 전자/스캔 두 종류를
  // 함께 보여준다.
  const [showLaborContractCombinedModal, setShowLaborContractCombinedModal] = useState(false);
  const hasAnyLaborContract = laborContracts.length > 0 || !!scanContractFilename;

  // 본인 확인 및 출력 — 계약서 PDF를 열람하면서(인증 blob 방식, openHrFile과 동일) 아직
  // 출력 확인 전(status !== 'issued')이면 서버에 출력완료를 알려 발급이력에 기록되게 한다.
  const printLaborContract = (item) => {
    setPrintingContractId(item.id);
    setPreviewModal({ title: `${item.contract_year}년 ${item.contract_month}월 ${item.contract_day}일 근로계약서`, filename: item.filename });
    (item.status === 'issued' ? Promise.resolve() : API.post(`/mypage/labor-contract/${item.id}/print`).then(() => loadLaborContracts()))
      .catch(() => showMsg('출력 확인 처리 중 오류가 발생했습니다.', 'error'))
      .finally(() => setPrintingContractId(null));
  };

  // 요청사항: "서명이 완료되면 다시보기가 아니라 출력하기, 보기 버튼으로 구성" — 이미 출력완료된
  // 근로계약서나 서명완료된 근로서약서/개인정보동의서는 더 이상 "다시 보기" 한 버튼이 아니라
  // "출력하기"(실제 인쇄창을 바로 띄움)와 "보기"(미리보기 모달만 염, 부수효과 없음) 두 버튼으로
  // 나눈다. 출력하기는 Sales.js/ShipmentDocPreview.js 등에서 이미 쓰던 방식(화면 밖 숨은
  // iframe에 blob PDF를 띄워 그 인쇄창을 그대로 여는 방식)을 그대로 재사용한다.
  const printHrFile = (filename, logInfo) => {
    // logInfo = { docType, refId } — 근로계약서(전자)/근로서약서/개인정보동의서를 출력할 때마다
    // 서버에 출력이력 한 줄을 남긴다(실패해도 실제 출력 자체는 막지 않도록 fire-and-forget).
    if (logInfo && logInfo.docType) {
      API.post('/mypage/doc-print-log', { docType: logInfo.docType, refId: logInfo.refId, filename }).catch(() => {});
    }
    API.get(`/hr/file/${encodeURIComponent(filename)}`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
        iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
        iframe.src = url;
        iframe.onload = () => { setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 600); };
        document.body.appendChild(iframe);
        setTimeout(() => { try { iframe.remove(); } catch (e) {} try { URL.revokeObjectURL(url); } catch (e) {} }, 60000);
      })
      .catch(() => showMsg('출력할 파일을 불러오지 못했습니다.', 'error'));
  };

  // 전자서명 — 관리자가 인사관리에서 "서명요청"을 누르면 signature_status가 'requested'로
  // 바뀌어 여기에 "서명하기" 버튼이 뜬다. 등록된 서명(계정관리)을 그대로 쓰거나, 이 계약서
  // 전용으로 새 PNG를 첨부해 서명할 수 있다.
  const [signModalItem, setSignModalItem] = useState(null);
  const [signSubmitting, setSignSubmitting] = useState(false);

  const submitSignature = (useProfile, file) => {
    if (!signModalItem) return;
    setSignSubmitting(true);
    let req;
    if (file) {
      const fd = new FormData();
      fd.append('file', file);
      req = API.post(`/mypage/labor-contract/${signModalItem.id}/sign`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      req = API.post(`/mypage/labor-contract/${signModalItem.id}/sign`, { useProfileSignature: true });
    }
    req
      .then(() => { showMsg('서명이 완료되었습니다.', 'success'); setSignModalItem(null); loadLaborContracts(); })
      .catch(err => showMsg(err.response?.data?.message || '서명 처리 중 오류가 발생했습니다.', 'error'))
      .finally(() => setSignSubmitting(false));
  };

  // 근로서약서 / 개인정보 제공 동의서 전자서명 — 근로계약(전자)을 생성하거나 근로계약(스캔)을
  // 업로드하는 시점에(그 사원에게 처음 발생하는 경우에만) 서버가 서명란을 비운 채 자동으로
  // 이 두 문서를 만들어둔다(hr.js의 autoCreateEsignDocs). 근로자는 근로계약서 서명과 완전히
  // 동일한 방식(등록된 서명 재사용 또는 이 문서 전용 PNG 첨부)으로 서명을 완료한다.
  const [esignDocs, setEsignDocs] = useState([]);
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [esignSignItem, setEsignSignItem] = useState(null);

  const loadEsignDocs = () => {
    API.get('/mypage/esign-docs').then(r => setEsignDocs(r.data || [])).catch(() => setEsignDocs([]));
  };

  const pledgeDocs = esignDocs.filter(d => d.doc_type === 'pledge');
  const privacyDocs = esignDocs.filter(d => d.doc_type === 'privacy_consent');

  const viewEsignDoc = (item) => {
    setPreviewModal({ title: item.doc_type === 'pledge' ? '근로서약서' : '개인정보 제공 동의서', filename: item.filename });
  };

  // 근로서약서/개인정보동의서 모달 내부의 문서 목록 행(보통 1건) — 서명대기/서명완료 상태와
  // 보기·서명하기 버튼을 함께 보여준다. 근로계약서 통합 모달의 행 UI와 동일한 스타일이다.
  const renderEsignDocRows = (docs) => docs.map(d => (
    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 14px', background: '#f8fafc', border: '1px solid #eee', borderRadius: 8, fontSize: 13 }}>
      <span>
        {d.status === '서명완료'
          ? <b style={{ color: '#059669' }}>[서명완료]</b>
          : <b style={{ color: '#e0a05a' }}>[서명대기]</b>}
        {d.signed_at ? ` · ${d.signed_at} 서명` : ''}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {d.status !== '서명완료' && (
          <button onClick={() => setEsignSignItem(d)} style={{ ...miniBtn, color: '#7c3aed', borderColor: '#7c3aed' }}>
            ✍️ 서명하기
          </button>
        )}
        {d.status === '서명완료' && (
          <button onClick={() => printHrFile(d.filename, { docType: d.doc_type, refId: d.id })} style={miniBtn}>🖨️ 출력하기</button>
        )}
        <button onClick={() => viewEsignDoc(d)} style={miniBtn}>보기</button>
      </div>
    </div>
  ));

  useEffect(() => {
    loadInfo(); loadRequests(); loadLeaveBalance(); loadLaborContracts(); loadScanContract(); loadEsignDocs();
    loadAttendanceList(); loadLeavesList();
    // 2026-09: "관리자지정으로 관리자 권한이 부여된 경우 계정관리에서 위 설정이 가능하도록
    // 기능개선" 요청에 따라 회원 리스트 데이터 로딩도 isTrueSuperAdmin(리터럴)이 아니라
    // isSuperAdmin(admin 포함)일 때 실행한다 — 관리자 권한을 부여받은 계정도 이 화면을
    // 그대로 사용할 수 있어야 한다. 백엔드(admin.js router.use(requireAccountManagementAccess))
    // 도 이미 admin 역할을 최고관리자와 동일하게 통과시키도록 되어 있어 별도 백엔드 수정은
    // 필요 없다.
    if (isSuperAdmin) { loadMemberList(); loadMemberOrg(); loadMemberPerms(); loadEditRequests(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 회원 리스트 — 2026-08 후속 요청: "회원가입이 발생하면 승인/거부 내용을 목록에 나타나게" —
  // 예전에는 승인된 계정만 보여주고 승인대기는 인사관리 > 사원정보에서 따로 처리했는데, 이제
  // 승인대기 회원도 이 목록에 함께 표시하고 바로 승인/거부할 수 있다(정렬은 승인대기가 먼저
  // 오도록 GET /admin/users가 이미 `ORDER BY u.approved ASC, u.created_at ASC`로 내려준다).
  // 또한 "생산현황판 전용 아이디도 계정관리 목록에 표시" 요청에 따라 전용계정(account_type===
  // 'display_only')도 더 이상 제외하지 않고 함께 보여준다(별도 "🖥️ 전용계정 관리" 버튼은 신규
  // 전용계정 생성용으로 계속 남겨둔다). 최고관리자(ninegro) 본인 계정만 스스로를 관리 대상으로
  // 다룰 필요가 없어 목록에서 제외한다.
  const visibleMembers = memberList.filter(u => u.role !== 'superadmin');
  const filteredMembers = memberSearch.trim()
    ? visibleMembers.filter(u => [u.name, u.username, u.department, u.position, u.employee_no]
        .some(v => (v || '').toString().toLowerCase().includes(memberSearch.trim().toLowerCase())))
    : visibleMembers;

  // 목록 컬럼헤더 클릭 → 오름차순/내림차순 토글 정렬(2026-08 추가). 등록일/사번/부서/
  // 직급/이름/아이디만 정렬 가능(구분/권한설정/관리자권한/비밀번호는 파생값·버튼 컬럼이라 제외).
  // 화면 진입 시 기본값은 "직급 순서"(memberSortKey 초기값 'position') — 인사관리 > 조직도의
  // 직급 관리에서 정한 순서(positions.sort_order, GET /org가 그 순서 그대로 내려주는 배열)를
  // 그대로 따른다. 알파벳/가나다 정렬이 아니라 실제 직급 서열(임원→부서장→팀원 등) 순서다.
  // 또한 "생산현황판 전용 아이디는 항상 목록에서 제일 아래쪽으로" 요청에 따라, 어떤 정렬
  // 기준을 선택하든 account_type==='display_only' 계정은 항상 맨 아래로 고정한다.
  const sortedMembers = useMemo(() => {
    const isDisplay = (u) => u.account_type === 'display_only';
    // 직급명 → 순위(0부터). memberPositions는 GET /org가 내려주는 {id, name, sort_order, ...}
    // 객체 배열이므로(문자열 배열이 아님) name 필드로 매칭해야 한다 — 이전 버전은 indexOf(문자열)로
    // 비교해 항상 -1(불일치)이 되면서 정렬이 전혀 동작하지 않던 버그가 있었다.
    // 배열 순서(=positions.sort_order 오름차순)가 곧 실제 직급 서열(높은 직급이 먼저)이다.
    // 직급 목록에 없거나 미지정인 경우 맨 뒤로 보낸다.
    const positionRank = (u) => {
      const idx = memberPositions.findIndex(p => p && p.name === u?.position);
      return idx === -1 ? memberPositions.length : idx;
    };
    if (!memberSortKey) {
      const normal = filteredMembers.filter(u => !isDisplay(u));
      const display = filteredMembers.filter(isDisplay);
      return [...normal, ...display];
    }
    const copy = [...filteredMembers];
    copy.sort((a, b) => {
      const aDisplay = isDisplay(a), bDisplay = isDisplay(b);
      if (aDisplay !== bDisplay) return aDisplay ? 1 : -1;
      let cmp;
      if (memberSortKey === 'position') {
        cmp = positionRank(a) - positionRank(b);
      } else {
        let va = a ? a[memberSortKey] : null;
        let vb = b ? b[memberSortKey] : null;
        if (va === null || va === undefined) va = '';
        if (vb === null || vb === undefined) vb = '';
        const na = Number(va), nb = Number(vb);
        if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
        else cmp = String(va).localeCompare(String(vb), 'ko');
      }
      return memberSortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filteredMembers, memberSortKey, memberSortDir, memberPositions]);

  const toggleMemberSort = (key) => {
    if (!key) return;
    if (memberSortKey === key) setMemberSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setMemberSortKey(key); setMemberSortDir('asc'); }
  };

  const submitGrantAdmin = (u) => {
    setGrantingId(u.id);
    API.patch(`/admin/users/${u.id}/grant-admin`)
      .then(r => { showMsg(r.data?.message || '관리자 권한을 부여했습니다.', 'success'); loadMemberList(); })
      .catch(err => showMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error'))
      .finally(() => setGrantingId(null));
  };

  const submitRevokeAdmin = (u) => {
    if (!window.confirm(`${u.name}님의 관리자 권한을 회수하시겠습니까?`)) return;
    setRevokingId(u.id);
    API.patch(`/admin/users/${u.id}/revoke-admin`)
      .then(r => { showMsg(r.data?.message || '관리자 권한을 회수했습니다.', 'success'); loadMemberList(); })
      .catch(err => showMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error'))
      .finally(() => setRevokingId(null));
  };

  // 계정관리(구) 모달에 있던 회원구분/부서/직급 변경 3종 — 목록의 각 열에서 바로 처리하도록
  // "풀어놓은" 버전. API 경로/바디는 HRManagement.js의 changeMemberType/changeDepartmentAcct/
  // changePositionAcct와 동일하다.
  const changeMemberType = (u, value) => {
    setSavingFieldId(`${u.id}-type`);
    API.patch(`/admin/users/${u.id}/member-type`, { memberType: value })
      .then(() => { showMsg('회원구분이 변경되었습니다.'); loadMemberList(); })
      .catch(err => alert(err.response?.data?.message || '변경 실패'))
      .finally(() => setSavingFieldId(null));
  };
  const changeMemberDept = (u, value) => {
    setSavingFieldId(`${u.id}-dept`);
    API.patch(`/admin/users/${u.id}/department`, { department: value })
      .then(() => { showMsg('부서가 변경되었습니다.'); loadMemberList(); })
      .catch(err => alert(err.response?.data?.message || '변경 실패'))
      .finally(() => setSavingFieldId(null));
  };
  const changeMemberPos = (u, value) => {
    setSavingFieldId(`${u.id}-pos`);
    API.patch(`/admin/users/${u.id}/position`, { position: value })
      .then(() => { showMsg('직급이 변경되었습니다.'); loadMemberList(); })
      .catch(err => alert(err.response?.data?.message || '변경 실패'))
      .finally(() => setSavingFieldId(null));
  };
  // 비밀번호 초기화 — 확인창 없이 클릭 즉시 고정 비밀번호(1q2w3e4r)로 초기화 (기존 계정관리
  // 모달과 동일한 동작).
  const resetMemberPassword = (u) => {
    setResettingId(u.id);
    API.patch(`/admin/users/${u.id}/reset-password`)
      .then(() => alert('비밀번호가 1q2w3e4r 로 초기화되었습니다.'))
      .catch(err => alert(err.response?.data?.message || '비밀번호 초기화에 실패했습니다.'))
      .finally(() => setResettingId(null));
  };

  // 신규가입 승인/거부 — 인사관리 > 사원정보 상단 "⏳ 승인 대기"와 동일한 API를 그대로
  // 재사용한다(요청사항 3: "회원가입이 발생하면 승인, 거부 내용을 목록에 나타날 수 있게").
  const approveMember = (u) => {
    setApprovingId(u.id);
    API.patch(`/admin/users/${u.id}/approve`)
      .then(() => { showMsg(`${u.name}님의 가입을 승인했습니다.`); loadMemberList(); })
      .catch(err => showMsg(err.response?.data?.message || '승인 처리 중 오류가 발생했습니다.', 'error'))
      .finally(() => setApprovingId(null));
  };
  const rejectMember = (u) => {
    if (!window.confirm(`${u.name}님의 가입 신청을 거부하시겠습니까?`)) return;
    setRejectingId(u.id);
    API.patch(`/admin/users/${u.id}/reject`)
      .then(() => { showMsg(`${u.name}님의 가입을 거부했습니다.`); loadMemberList(); })
      .catch(err => showMsg(err.response?.data?.message || '거부 처리 중 오류가 발생했습니다.', 'error'))
      .finally(() => setRejectingId(null));
  };

  // 회원 정보수정 요청 승인/거부 — 요청사항 2: "각 회원이 정보를 수정할 경우 목록에 나타날 수
  // 있게". 마이페이지 본인 화면의 "정보수정"(handleEditRequest → POST /mypage/request-edit)이
  // 여기 대기 목록에 뜨며, 승인하면 해당 필드가 users 테이블에 즉시 반영된다(AdminUsers.js의
  // 기존 패턴과 동일한 API 재사용).
  const approveEditRequest = (id) => {
    setEditReqActingId(id);
    API.patch(`/admin/edit-requests/${id}/approve`)
      .then(() => { showMsg('수정 요청을 승인했습니다.'); loadEditRequests(); loadMemberList(); })
      .catch(err => showMsg(err.response?.data?.message || '승인 처리 중 오류가 발생했습니다.', 'error'))
      .finally(() => setEditReqActingId(null));
  };
  const rejectEditRequest = (id) => {
    setEditReqActingId(id);
    API.patch(`/admin/edit-requests/${id}/reject`)
      .then(() => { showMsg('수정 요청을 거부했습니다.', 'error'); loadEditRequests(); })
      .catch(err => showMsg(err.response?.data?.message || '거부 처리 중 오류가 발생했습니다.', 'error'))
      .finally(() => setEditReqActingId(null));
  };

  // 수정 요청 제출 — 관리자 권한(최고관리자/부여받은 관리자)이면 백엔드가 승인 절차 없이
  // 즉시 반영하고, 응답 메시지도 그에 맞게 다르게 돌아온다.
  const handleEditRequest = async () => {
    if (!editField || !editValue.trim()) { showMsg('수정할 내용을 입력해주세요.', 'error'); return; }
    setLoading(true);
    try {
      const r = await API.post('/mypage/request-edit', { field_name: editField, new_value: editValue });
      showMsg(r.data?.message || (isAdminUser ? '관리자 권한으로 즉시 반영되었습니다.' : '수정 요청이 전송됐습니다. 관리자 승인을 기다려주세요.'), 'success');
      setEditField(''); setEditValue('');
      loadRequests();
      if (isAdminUser) loadInfo();
    } catch(err) {
      showMsg(err.response?.data?.message || '오류 발생', 'error');
    } finally { setLoading(false); }
  };

  // 수정 요청 취소 — 아직 '대기' 상태인 내 요청만 취소할 수 있다.
  const [cancelingId, setCancelingId] = useState(null);
  const cancelRequest = async (id) => {
    if (!window.confirm('이 수정 요청을 취소하시겠습니까?')) return;
    setCancelingId(id);
    try {
      await API.delete(`/mypage/requests/${id}`);
      showMsg('수정 요청을 취소했습니다.', 'success');
      loadRequests();
    } catch (err) {
      showMsg(err.response?.data?.message || '취소 중 오류가 발생했습니다.', 'error');
    } finally { setCancelingId(null); }
  };

  // 비밀번호 변경
  const handleChangePw = async () => {
    if (!pwForm.currentPassword || !pwForm.newPassword) { showMsg('모든 항목을 입력해주세요.', 'error'); return; }
    if (pwForm.newPassword !== pwForm.confirmPassword) { showMsg('새 비밀번호가 일치하지 않습니다.', 'error'); return; }
    if (pwForm.newPassword.length < 6) { showMsg('새 비밀번호는 6자 이상이어야 합니다.', 'error'); return; }
    setLoading(true);
    try {
      const res = await API.patch('/admin/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      showMsg(res.data.message + ' 3초 후 로그아웃됩니다.', 'success');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPwModal(false);
      setTimeout(() => logout(), 3000);
    } catch(err) {
      showMsg(err.response?.data?.message || '오류 발생', 'error');
    } finally { setLoading(false); }
  };

  // 추가정보(인사기록카드) 불러오기 + 모달 열기
  const openHrCardModal = () => {
    setHrCardLoading(true);
    API.get('/mypage/hr-card')
      .then(r => { setHrCardForm(r.data || {}); setShowHrCardModal(true); })
      .catch(() => showMsg('추가정보를 불러오지 못했습니다.', 'error'))
      .finally(() => setHrCardLoading(false));
  };

  // 추가정보(인사기록카드) 저장 — 승인 절차 없이 즉시 반영
  const saveHrCard = async () => {
    setHrCardSaving(true);
    try {
      await API.put('/mypage/hr-card', hrCardForm || {});
      showMsg('추가정보가 저장되었습니다.', 'success');
      setShowHrCardModal(false);
    } catch(err) {
      showMsg(err.response?.data?.message || '오류 발생', 'error');
    } finally { setHrCardSaving(false); }
  };

  // 추가정보 모달의 주민등록등본/통장사본 등록(셀프 업로드) — 근로계약서는 회사가 작성해
  // 건네주는 문서라 자가등록 대상에서 제외했다(요청에도 이 2종만 명시됨). 업로드 즉시 반영되도록
  // hrCardForm의 doc_* 값을 서버가 돌려준 파일명으로 갱신한다.
  const uploadHrCardDoc = (docType) => (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/mypage/hr-card/document/${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => {
        setHrCardForm(v => ({ ...v, [`doc_${docType}`]: r.data.filename }));
        showMsg('등록되었습니다.', 'success');
      });
  };

  // 서명 이미지 업로드(PNG만) — 성공하면 info.signature_path를 갱신해 미리보기에 바로 반영한다.
  // FileDropZone은 별도 disabled 상태를 지원하지 않으므로(드래그앤드롭 전환, 2026-08), 업로드
  // 중 중복 제출을 여기서 직접 막는다(기존 <input disabled={sigUploading}>과 동일한 효과).
  const uploadSignature = (file) => {
    if (!file || sigUploading) return;
    if (file.type !== 'image/png' && !/\.png$/i.test(file.name || '')) {
      showMsg('PNG 파일만 등록할 수 있습니다.', 'error'); return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setSigUploading(true);
    API.post('/mypage/signature', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => { setInfo(v => ({ ...v, signature_path: r.data.signature_path })); showMsg('서명이 등록되었습니다.', 'success'); })
      .catch(err => showMsg(err.response?.data?.message || '오류 발생', 'error'))
      .finally(() => setSigUploading(false));
  };
  const deleteSignature = () => {
    if (!window.confirm('등록된 서명을 삭제하시겠습니까?')) return;
    API.delete('/mypage/signature')
      .then(() => { setInfo(v => ({ ...v, signature_path: null })); showMsg('서명이 삭제되었습니다.', 'success'); })
      .catch(err => showMsg(err.response?.data?.message || '오류 발생', 'error'));
  };

  // 홈 화면의 "회원가입 후 첫 로그인" 안내 배너에서 "📇 추가정보 입력" 버튼을 누르면
  // navigate('/mypage', { state: { openHrCard: true } })로 넘어온다 — 여기서 그 신호를 받아
  // 추가정보 모달을 자동으로 연다. 한 번 처리하면 state를 비워서(replace) 새로고침 시 다시
  // 열리지 않게 한다(다른 메뉴의 "activityOpen" 자동열림과 동일한 패턴).
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.state?.openHrCard) {
      openHrCardModal();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // 요청사항: "근로계약 서명요청후 근로자가 로그인해서 모달창에 서명하기를 누르면 바로
  // 계정관리 - 근로계약서 항목이 모달창으로 나타남" — LaborContractSignAlert.js가
  // navigate('/mypage', { state: { openLaborContract: true } })로 넘어오면 여기서 그 신호를
  // 받아 근로계약서 통합 모달을 자동으로 연다. openHrCard와 동일한 패턴.
  useEffect(() => {
    if (location.state?.openLaborContract) {
      setShowLaborContractCombinedModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 10, outline: 'none' };
  const lbl = { fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 };
  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px', marginBottom: 16 };
  const miniBtn = { background: '#fff', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 8,
    padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };

  const ROLE_LABEL = { admin: '관리자', partner: '협력사', worker: '현장관리자' };
  const STATUS_COLOR = { '대기': '#d97706', '승인': '#16a34a', '거부': '#dc2626' };
  const STATUS_BG    = { '대기': '#fef3c7', '승인': '#dcfce7', '거부': '#fee2e2' };

  return (
    <div style={{ padding: 24, maxWidth: 1690 }}>
      {/* 헤더 — 다른 메뉴(스토리지/계약관리/영업관리 등)와 동일한 제목/설명 여백 규칙(h2 margin:0 +
          p margin:'4px 0 0')을 그대로 맞춤. 연차현황/전용계정 관리 버튼은 제목 우측에 배치. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
            👤 계정관리
            {/* 진짜 최고관리자(ninegro, role==='superadmin')로 로그인했을 때만 표시 — 관리자
                권한을 부여받은 계정(role==='admin')에는 표시하지 않는다(2026-08 추가). */}
            {isTrueSuperAdmin && <span style={{ fontSize: 18, fontWeight: 600, color: '#993556', marginLeft: 8 }}>- 최고관리자</span>}
          </h2>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>내 정보를 확인하고 수정을 요청할 수 있습니다.</p>
        </div>
        <div className="hdr-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {/* 최고관리자(ninegro, isTrueSuperAdmin)는 직원이 아니므로 연차가 없다 — 연차현황
              카드는 실제 직원(관리자 권한을 부여받은 계정 포함)에게만 표시(2026-08 추가).
              2026-09 추가: "관리자모드로 바뀌면 연차 카드를 숨겨달라" — 관리자모드(회원 리스트
              화면)에서는 본인 정보와 무관한 관리 화면이므로 이 배지도 함께 숨긴다. */}
          {leaveBalance && !isTrueSuperAdmin && !showAdminCard && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 18px', flexShrink: 0 }}>
              <span style={{ fontSize: 13, color: '#333' }}>
                🌴 {leaveBalance.year}년 연차 <b style={{ color: '#185FA5', fontSize: 15 }}>{leaveBalance.remaining_days}일</b> 남음
              </span>
            </div>
          )}
          {/* 회원 검색 — 예전에는 회원 목록 카드 안쪽 헤더에 있었는데(2026-08), "이름/아이디
              검색창을 비밀번호 변경 버튼 왼쪽으로 이동" 요청에 따라 페이지 상단 헤더로 옮기고
              비밀번호 변경 버튼 바로 왼쪽에 배치한다. 최고관리자 화면에서만 의미가 있는
              검색창이라 isTrueSuperAdmin일 때만 보여준다. */}
          {showAdminCard && (
            <input placeholder="이름/아이디/부서/사번 검색" value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
              style={{ ...inp, width: 200, marginBottom: 0, padding: '9px 12px' }} />
          )}
          {/* 관리자모드/사용자모드 토글 — 2026-09 요청: "관리자 권한이 부여되더라도 기존
              계정정보는 그대로 있어야 한다" — 관리자 권한을 부여받은 계정(role==='admin')이
              계정관리에 들어가면 무조건 회원 관리 화면으로 바뀌어버려 본인 계정정보(현재 내
              정보/근태현황/연차사용현황)를 볼 방법이 없었다. 비밀번호 변경 버튼 왼쪽에 토글을
              두어 사용자모드(본인 정보)/관리자모드(회원 리스트 관리)를 오갈 수 있게 한다.
              isSuperAdmin(최고관리자+관리자 권한 부여계정)에게만 노출한다. */}
          {isSuperAdmin && (
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              {[['user', '🙋 사용자모드'], ['admin', '🛠️ 관리자모드']].map(([mode, label]) => (
                <button key={mode} className="keep" onClick={() => setAdminViewMode(mode)}
                  style={{ border: '1px solid #1e293b', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    background: adminViewMode === mode ? '#1e293b' : '#fff', color: adminViewMode === mode ? '#fff' : '#1e293b' }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* 🏢 회사정보 — 2026-09-14: "환경설정" 메뉴 신설에 따라 이 버튼/모달은 그쪽으로
              이동했다(계정관리 바로 아래 새 메뉴, frontend/src/pages/Settings.js 참고). */}
          {/* 🔑 비밀번호 변경 — 예전에 이 자리에 있던 "🖥️ 전용계정 관리" 버튼은 삭제하고
              (2026-08), 대신 비밀번호 변경 버튼을 이 위치로 이동. 클릭하면 모달창에서
              변경 폼을 입력한다(예전에는 화면 맨 아래 항상 펼쳐진 카드였음). 버튼 크기는
              다른 메뉴(계약관리 "계약등록" 등)의 헤더 우측 버튼과 동일하게 맞춘다.  */}
          <button onClick={() => setShowPwModal(true)}
            style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            🔑 비밀번호 변경
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msgType === 'success' ? '#d1fae5' : '#fee2e2',
          color:      msgType === 'success' ? '#065f46' : '#dc2626' }}>
          {msg}
        </div>
      )}

      {/* 최고관리자(ninegro) 또는 관리자지정으로 관리자 권한을 부여받은 계정(role==='admin')이
          "관리자모드"를 선택했을 때만 "현재 내 정보" 카드를 "회원 리스트(계정관리)" 카드로
          대체한다(요청사항: 구분/회사명/아이디/서명등록/정보수정/추가정보 등은 삭제). 2026-09
          재수정: 관리자 권한이 부여되더라도 기존 계정정보(현재 내 정보/근태현황/연차사용현황)가
          사라지면 안 되므로, isSuperAdmin이라는 이유만으로 무조건 회원 리스트를 보여주던 것을
          adminViewMode 토글 기반으로 바꾼다 — "사용자모드"에서는 아래 일반 회원과 동일하게
          본인 정보 카드가 보이고, "관리자모드"에서만 회원 리스트가 보인다(showAdminCard 참고).
          단, 관리자 권한 부여/회수 버튼 자체는 여전히 최고관리자만 쓸 수 있다(MemberRow의
          canManageAdmin prop 참고). 일반 회원(관리자 권한이 전혀 없는 계정)은 토글 자체가
          보이지 않으므로 항상 기존 "현재 내 정보" 카드를 그대로 사용한다. */}
      {showAdminCard ? (
        <div style={card}>
          {/* "계정관리" 제목 / "전체 회원 목록입니다..." 안내문구는 삭제(2026-08) — 검색창은
              페이지 상단 헤더의 비밀번호 변경 버튼 왼쪽으로 이동했다. */}

          {/* 요청사항 2: "각 회원이 정보를 수정할 경우 목록에 나타날 수 있게" — 마이페이지
              본인 화면에서 올린 정보수정 요청(대기중)을 회원 목록 위에 별도로 보여주고
              그 자리에서 바로 승인/거부한다(AdminUsers.js의 기존 패턴 재사용). */}
          {editRequests.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309', marginBottom: 8 }}>
                ✏️ 정보 수정 요청 ({editRequests.length}건) — 승인 대기 중
              </div>
              {editRequests.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 10,
                  padding: '10px 14px', marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ fontSize: 12.5 }}>
                    <span style={{ fontWeight: 600 }}>{r.user_name}</span>
                    <span style={{ color: '#888', margin: '0 6px' }}>(@{r.username})</span>
                    <span style={{ color: '#aaa' }}>→</span>
                    <span style={{ margin: '0 6px', background: '#E6F1FB', color: '#185FA5',
                      padding: '2px 8px', borderRadius: 6, fontSize: 11.5, fontWeight: 500 }}>
                      {FIELD_LABEL[r.field_name] || r.field_name}
                    </span>
                    <span style={{ color: '#aaa' }}>변경 요청:</span>
                    <span style={{ margin: '0 6px', fontWeight: 600, color: '#1a1a1a' }}>{r.new_value}</span>
                    {r.old_value && (<span style={{ fontSize: 11, color: '#aaa' }}>(기존: {r.old_value})</span>)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#aaa' }}>{r.created_at?.slice(0, 10)}</span>
                    <button onClick={() => approveEditRequest(r.id)} disabled={editReqActingId === r.id}
                      style={{ ...memActionBtn, color: '#185FA5', borderColor: '#185FA5' }}>✅ 승인</button>
                    <button onClick={() => rejectEditRequest(r.id)} disabled={editReqActingId === r.id}
                      style={{ ...memActionBtn, color: '#dc2626', borderColor: '#dc2626' }}>❌ 거부</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredMembers.length === 0 ? (
            <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>표시할 회원이 없습니다.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
                <thead>
                  <tr>
                    <MemSortTh label="구분" />
                    <MemSortTh label="등록일" sortKey="created_at" currentKey={memberSortKey} currentDir={memberSortDir} onSort={toggleMemberSort} />
                    <MemSortTh label="사번" sortKey="employee_no" currentKey={memberSortKey} currentDir={memberSortDir} onSort={toggleMemberSort} />
                    <MemSortTh label="부서" sortKey="department" currentKey={memberSortKey} currentDir={memberSortDir} onSort={toggleMemberSort} />
                    <MemSortTh label="직급" sortKey="position" currentKey={memberSortKey} currentDir={memberSortDir} onSort={toggleMemberSort} />
                    <MemSortTh label="이름" sortKey="name" currentKey={memberSortKey} currentDir={memberSortDir} onSort={toggleMemberSort} align="left" />
                    <MemSortTh label="아이디" sortKey="username" currentKey={memberSortKey} currentDir={memberSortDir} onSort={toggleMemberSort} align="left" />
                    <MemSortTh label="접속로그" />
                    <MemSortTh label="권한설정" />
                    <MemSortTh label="관리자권한" />
                    <MemSortTh label="비밀번호" />
                  </tr>
                </thead>
                <tbody>
                  {sortedMembers.map(u => (
                    <MemberRow key={u.id} u={u} departments={memberDepartments} positions={memberPositions}
                      onChangeType={changeMemberType} onChangeDept={changeMemberDept} onChangePos={changeMemberPos}
                      onGrant={submitGrantAdmin} onRevoke={submitRevokeAdmin} onResetPassword={resetMemberPassword}
                      onOpenPerm={(user) => setPermUser(user)} onOpenLoginLog={(user) => setLoginLogUser(user)}
                      onApprove={approveMember} onReject={rejectMember}
                      savingFieldId={savingFieldId} grantingId={grantingId} revokingId={revokingId} resettingId={resettingId}
                      approvingId={approvingId} rejectingId={rejectingId} canManageAdmin={isTrueSuperAdmin} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>📋 현재 내 정보</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* 요청사항: "계정관리-서명등록 왼쪽에 근로계약 버튼을 자동으로 생성" — 전자근로계약
                  또는 근로계약(스캔) 원문이 하나라도 있을 때만 노출된다. */}
              {/* 근로계약서/근로서약서/개인정보동의서 — 세 문서 모두 근로계약(전자) 생성 또는
                  근로계약(스캔) 업로드 시점에 함께 준비되며, 각 문서를 개별적으로 확인·서명할 수
                  있도록 버튼을 나란히 배치한다(요청사항: "서명란 옆에 세가지 메뉴"). */}
              {hasAnyLaborContract && (
                <button onClick={() => setShowLaborContractCombinedModal(true)} style={miniBtn}>📝 근로계약서</button>
              )}
              {pledgeDocs.length > 0 && (
                <button onClick={() => setShowPledgeModal(true)} style={miniBtn}>📝 근로서약서</button>
              )}
              {privacyDocs.length > 0 && (
                <button onClick={() => setShowPrivacyModal(true)} style={miniBtn}>📝 개인정보동의서</button>
              )}
              <button onClick={() => setShowSigModal(true)} style={miniBtn}>🖊️ 서명등록</button>
              <button onClick={() => setShowInfoEditModal(true)} style={miniBtn}>✏️ 정보수정</button>
              <button onClick={openHrCardModal} disabled={hrCardLoading} style={miniBtn}>
                {hrCardLoading ? '불러오는 중...' : '📇 추가정보'}
              </button>
            </div>
          </div>
          {/* 3열 배치: 1행 구분/회사명/아이디, 2행 부서/직급/이름, 3행 연락처/긴급연락처/이메일,
              4행 주소(내용이 길 수 있어 한 줄 전체 사용) */}
          {info ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 20px' }}>
              {[
                ['구분', ROLE_LABEL[info.role] || info.role],
                ['회사명', info.company],
                ['아이디', info.username],
                ['부서', info.department],
                ['직급', info.position],
                ['이름', info.name],
                ['연락처', info.phone],
                ['긴급연락처', info.emergency_phone],
                ['이메일', info.email],
              ].map(([label, val]) => (
                <div key={label} style={{ fontSize: 13 }}>
                  <span style={{ color: '#888', marginRight: 6 }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{val || '-'}</span>
                </div>
              ))}
              <div style={{ fontSize: 13, gridColumn: '1 / -1' }}>
                <span style={{ color: '#888', marginRight: 6 }}>주소</span>
                <span style={{ fontWeight: 500 }}>{info.address || '-'}</span>
              </div>
            </div>
          ) : (
            <div style={{ color: '#aaa', fontSize: 13 }}>불러오는 중...</div>
          )}
        </div>
      )}

      {/* 근태현황/연차사용현황 — "현재 내 정보" 카드 아래쪽에 배치. 최고관리자(ninegro)는
          직원이 아니므로(연차현황 배지도 동일 조건으로 숨김) 노출하지 않는다. 2026-09 추가:
          "관리자모드"로 회원 리스트가 보이는 동안은 본인 정보 카드와 함께 숨긴다(showAdminCard
          참고) — 관리자모드/사용자모드 전환에 맞춰 같이 전환된다. */}
      {!showAdminCard && !isTrueSuperAdmin && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>🕒 근태현황</div>
              <div style={{ fontSize: 12, color: '#999' }}>최근 1개월</div>
            </div>
            {loadingAttendance ? (
              <div style={{ color: '#aaa', fontSize: 13 }}>불러오는 중...</div>
            ) : attendanceList.length === 0 ? (
              <div style={{ color: '#aaa', fontSize: 13 }}>근태 기록이 없습니다.</div>
            ) : (
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: '#888', borderBottom: '1px solid #eee' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>날짜</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>출근</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>퇴근</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceList.map((a, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '4px 6px' }}>{a.date}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{a.check_in ? a.check_in.slice(0, 5) : '-'}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{a.check_out ? a.check_out.slice(0, 5) : '-'}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{ATT_STATUS_LABEL[a.status] || a.status || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>🌴 연차사용현황</div>
              {leaveBalance && (
                <div style={{ fontSize: 12, color: '#666' }}>
                  {leaveBalance.year}년 부여 <b>{leaveBalance.granted_days}일</b> / 사용 <b>{leaveBalance.used_days}일</b> / 잔여 <b style={{ color: '#185FA5' }}>{leaveBalance.remaining_days}일</b>
                </div>
              )}
            </div>
            {loadingLeaves ? (
              <div style={{ color: '#aaa', fontSize: 13 }}>불러오는 중...</div>
            ) : leavesList.length === 0 ? (
              <div style={{ color: '#aaa', fontSize: 13 }}>올해 사용한 연차 내역이 없습니다.</div>
            ) : (
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: '#888', borderBottom: '1px solid #eee' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>기간</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>종류</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>일수</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leavesList.map(l => (
                      <tr key={l.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '4px 6px' }}>{l.start_date}{l.end_date && l.end_date !== l.start_date ? ` ~ ${l.end_date}` : ''}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{l.leave_type || '-'}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{l.days}일</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center', color: LEAVE_STATUS_COLOR[l.status] || '#666' }}>{l.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 근로계약 통합 모달 — 헤더의 "📝 근로계약" 버튼으로 연다. 전자근로계약(관리자가
          인사관리에서 발급한 계약서 목록)과 근로계약(스캔, 원문 그대로 업로드된 파일 1건)을
          한 화면에서 함께 확인/출력할 수 있다(요청사항 2.1.4.2/2.1.4.3, 2.2.2). */}
      {showLaborContractCombinedModal && (
        <Modal title="📝 근로계약" onClose={() => setShowLaborContractCombinedModal(false)} width={760}>
          {laborContracts.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 8 }}>근로계약(전자)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: scanContractFilename ? 18 : 0 }}>
                {laborContracts.map(h => (
                  <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 14px', background: '#f8fafc', border: '1px solid #eee', borderRadius: 8, fontSize: 13 }}>
                    <span>
                      {h.status === 'issued' ? (
                        <b style={{ color: '#059669' }}>[출력완료]</b>
                      ) : (
                        <b style={{ color: '#e0a05a' }}>[발급대기중]</b>
                      )}
                      {' '}{h.contract_year}년 {h.contract_month}월 {h.contract_day}일 근로계약서
                      {h.status === 'issued' && h.printed_at ? ` · ${h.printed_at} 출력확인` : ''}
                      {h.signature_status === 'signed' && <span style={{ color: '#059669', marginLeft: 6 }}>· ✅ 서명완료</span>}
                      {h.signature_status === 'requested' && <span style={{ color: '#7c3aed', marginLeft: 6 }}>· ✍️ 서명요청됨</span>}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {h.signature_status === 'requested' && (
                        <button onClick={() => setSignModalItem(h)} style={{ ...miniBtn, color: '#7c3aed', borderColor: '#7c3aed' }}>
                          ✍️ 서명하기
                        </button>
                      )}
                      {h.status === 'issued' ? (
                        <>
                          <button onClick={() => printHrFile(h.filename, { docType: 'labor_contract', refId: h.id })} style={miniBtn}>🖨️ 출력하기</button>
                          <button onClick={() => setPreviewModal({ title: `${h.contract_year}년 ${h.contract_month}월 ${h.contract_day}일 근로계약서`, filename: h.filename })} style={miniBtn}>
                            보기
                          </button>
                        </>
                      ) : (
                        <button onClick={() => printLaborContract(h)} disabled={printingContractId === h.id}
                          style={{ ...miniBtn, opacity: printingContractId === h.id ? 0.6 : 1 }}>
                          {printingContractId === h.id ? '여는 중...' : '✅ 확인 및 출력'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {scanContractFilename && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 8 }}>근로계약(스캔)</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: '#f8fafc', border: '1px solid #eee', borderRadius: 8, fontSize: 13 }}>
                <span>회사에서 등록한 근로계약서 원문입니다.</span>
                <button onClick={printScanContract} disabled={printingScan}
                  style={{ ...miniBtn, opacity: printingScan ? 0.6 : 1, flexShrink: 0 }}>
                  {printingScan ? '여는 중...' : '✅ 확인 및 출력'}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* 서명하기 모달 — 관리자가 서명요청한 근로계약서에 전자서명을 완료한다. 요청사항에 따라
          문서 미리보기 + 서명란 위치 오버레이 버튼 방식으로 개편했다(LaborContractSignModal,
          이 파일 상단에 정의). 완료되면 서버가 계약서 문서에 서명을 합성해 같은 파일에 다시
          저장하고, 모달은 최신 문서를 즉시 다시 불러와 화면에 보여준다. */}
      {signModalItem && (
        <LaborContractSignModal
          item={signModalItem}
          hasProfileSignature={!!info?.signature_path}
          onClose={() => setSignModalItem(null)}
          onSigned={() => { showMsg('서명이 완료되었습니다.', 'success'); loadLaborContracts(); }}
        />
      )}

      {/* 근로서약서 / 개인정보동의서 모달 — 헤더의 각 버튼으로 연다. 보통 사원당 1건씩만
          존재하지만(자동생성이 최초 1회만 일어남), 근로계약서 통합 모달과 동일한 목록 UI를
          재사용해 일관된 사용경험을 준다. */}
      {showPledgeModal && (
        <Modal title="📝 근로서약서" onClose={() => setShowPledgeModal(false)} width={760}>
          {pledgeDocs.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{renderEsignDocRows(pledgeDocs)}</div>
          ) : (
            <div style={{ color: '#aaa', fontSize: 13 }}>등록된 문서가 없습니다.</div>
          )}
        </Modal>
      )}

      {showPrivacyModal && (
        <Modal title="📝 개인정보 제공 동의서" onClose={() => setShowPrivacyModal(false)} width={760}>
          {privacyDocs.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{renderEsignDocRows(privacyDocs)}</div>
          ) : (
            <div style={{ color: '#aaa', fontSize: 13 }}>등록된 문서가 없습니다.</div>
          )}
        </Modal>
      )}

      {/* 근로서약서/개인정보동의서 서명하기 모달 — 근로계약서 서명 모달과 완전히 동일한
          UX(문서 미리보기 + 서명란 위치 오버레이 버튼, 등록된 서명 재사용 또는 이 문서 전용
          PNG 첨부)로 개편했다(EsignDocSignModal, 이 파일 상단에 정의). */}
      {esignSignItem && (
        <EsignDocSignModal
          item={esignSignItem}
          hasProfileSignature={!!info?.signature_path}
          onClose={() => setEsignSignItem(null)}
          onSigned={() => { showMsg('서명이 완료되었습니다.', 'success'); loadEsignDocs(); }}
        />
      )}

      {/* 근로계약서/근로계약(원문)/근로서약서/개인정보동의서 미리보기 모달 — 예전에는
          window.open(blob URL)로 새 탭을 여는 방식이라 PDF 뷰어가 없는 환경에서 다운로드로
          바뀌어버리는 문제가 있었다. HRManagement.js의 FilePreviewModal을 그대로 재사용해
          앱 내부에서 바로 미리보기가 되도록 한다. */}
      {previewModal && (
        // 요청사항: "근로계약서/근로서약서/개인정보동의서 보기 화면 가로폭 870px"
        <FilePreviewModal title={previewModal.title} filename={previewModal.filename} width={870}
          onClose={() => setPreviewModal(null)} />
      )}

      {/* 서명등록 모달 — 전자결재 결재/승인 화면에서 도장처럼 표시되는 PNG 서명 이미지를
          등록/교체/삭제한다. 관리자 승인 없이 본인이 바로 반영한다(정보수정 요청과 달리
          계정 핵심정보가 아니라서 즉시 적용). */}
      {showSigModal && (
        <Modal title="🖊️ 서명등록" onClose={() => setShowSigModal(false)} width={400}>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 14, lineHeight: 1.6 }}>
            전자결재 결재/승인 화면에서 사용할 서명 이미지를 등록합니다. PNG 파일만 등록할 수
            있습니다(투명배경 권장).
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
            minHeight: 100, border: '1px dashed #ddd', borderRadius: 8, marginBottom: 14, padding: 10 }}>
            {info?.signature_path
              ? <img src={`/uploads/${info.signature_path}`} alt="서명" style={{ maxWidth: '100%', maxHeight: 90 }} />
              : <span style={{ fontSize: 12, color: '#aaa' }}>등록된 서명이 없습니다.</span>}
          </div>
          {/* 드래그앤드롭 + 클릭 선택 공용 컴포넌트(FileDropZone) — 인증관리 등 다른 화면과 동일한
              첨부 UI로 통일. 업로드 중에는 클릭/드롭이 먹더라도 uploadSignature 내부에서
              sigUploading 상태로 중복 제출을 막는다(기존 disabled 처리와 동일한 효과). */}
          <FileDropZone
            onFile={uploadSignature}
            accept="image/png,.png"
            fileName={sigUploading ? '업로드 중...' : undefined}
            currentLabel={info?.signature_path ? '등록된 서명 있음 — 교체하려면 새 PNG를 올려주세요' : undefined}
            style={{ marginBottom: 8 }}
          />
          {info?.signature_path && (
            <button onClick={deleteSignature} style={{ ...miniBtn, width: '100%', color: '#dc2626', borderColor: '#dc2626' }}>서명 삭제</button>
          )}
        </Modal>
      )}

      {/* 정보수정 모달 — 핵심 계정정보(이름/직급/회사명/주소/연락처/이메일/긴급연락처) 수정요청.
          관리자 승인이 필요하며, 기존 "정보 수정 요청" 카드의 로직을 그대로 모달로 옮긴 것. */}
      {showInfoEditModal && (
        <Modal title="✏️ 정보수정" onClose={() => setShowInfoEditModal(false)} width={480}>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 14, lineHeight: 1.6 }}>
            {isAdminUser
              ? '✅ 관리자 권한 계정은 별도 승인 없이 즉시 반영됩니다.'
              : '⚠️ 수정 요청 후 관리자가 승인해야 실제로 반영됩니다.'}
          </p>

          <label style={lbl}>수정할 항목 선택</label>
          <select value={editField} onChange={e => { setEditField(e.target.value); setEditValue(''); }} style={inp}>
            <option value="">-- 항목 선택 --</option>
            {Object.entries(FIELD_LABEL).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {editField && (
            <>
              <label style={lbl}>새로운 {FIELD_LABEL[editField]}</label>
              <input
                placeholder={'새로운 ' + FIELD_LABEL[editField] + ' 입력'}
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEditRequest()}
                style={inp}
              />
              {editField === 'email' && <SysMailGuideNote style={{ marginBottom: 12 }} />}
              <button onClick={handleEditRequest} disabled={loading}
                style={{ padding: '9px 18px', width: '100%', background: loading ? '#aaa' : '#185FA5', color: '#fff', border: 'none',
                  borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? '요청 중...' : '수정 요청 보내기'}
              </button>
            </>
          )}

          {requests.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>내 수정 요청 현황</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {requests.map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 13 }}>
                    <div>
                      <span style={{ color: '#888', marginRight: 6 }}>{FIELD_LABEL[r.field_name]}</span>
                      <span style={{ fontWeight: 500 }}>{r.new_value}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: '#aaa' }}>{r.created_at?.slice(0, 10)}</span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                        background: STATUS_BG[r.status], color: STATUS_COLOR[r.status] }}>
                        {r.status}
                      </span>
                      {r.status === '대기' && (
                        <button onClick={() => cancelRequest(r.id)} disabled={cancelingId === r.id}
                          style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 600,
                            cursor: cancelingId === r.id ? 'not-allowed' : 'pointer', padding: 0 }}>
                          {cancelingId === r.id ? '취소 중...' : '취소'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* 추가정보(인사기록카드) 모달 — 인사관리 > 직원등록과 완전히 동일한 입력폼(HrCardFields)을
          재사용한다. 사진 업로드만 본인 전용 엔드포인트(/mypage/hr-card/photo-upload)로 우회한다
          (직원등록 쪽 엔드포인트는 인사관리 메뉴 권한이 있어야 접근 가능하기 때문). */}
      {showHrCardModal && hrCardForm && (
        <Modal title="📇 추가정보 (인사기록카드)" onClose={() => setShowHrCardModal(false)} width={700}
          headerActions={
            <div style={{ display: 'flex', gap: 6 }}>
              <DocButton label="주민등록등본" filename={hrCardForm.doc_resident_reg} canWrite onUpload={uploadHrCardDoc('resident_reg')} />
              <DocButton label="급여계좌" filename={hrCardForm.doc_bankbook} canWrite onUpload={uploadHrCardDoc('bankbook')} />
            </div>
          }>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <HrCardFields f={hrCardForm} setF={setHrCardForm} disabled={false} photoUploadUrl="/mypage/hr-card/photo-upload" />
          </div>
          <button onClick={saveHrCard} disabled={hrCardSaving}
            style={{ marginTop: 14, padding: '10px 0', width: '100%', background: hrCardSaving ? '#aaa' : '#185FA5', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: hrCardSaving ? 'not-allowed' : 'pointer' }}>
            {hrCardSaving ? '저장 중...' : '저장'}
          </button>
        </Modal>
      )}

      {/* 계정관리 회원 리스트의 "🔐 권한설정" 버튼 — HRManagement.js의 PermissionModal/
          DetailPermissionModal을 그대로 재사용한다(예전 인사관리 > 사원정보 목록의 권한설정
          버튼과 완전히 동일한 구현). */}
      {permUser && (
        <PermissionModal
          user={permUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === permUser.username)}
          onClose={() => setPermUser(null)}
          onSaved={() => { setPermUser(null); showMsg('권한이 저장되었습니다.'); loadMemberPerms(); }}
          onOpenDetail={(menuKey, liveGranted) => {
            setDetailUser(permUser);
            setDetailMenuKey(menuKey);
            setDetailLiveOverride({ menu_key: menuKey, ...liveGranted });
          }}
        />
      )}
      {detailUser && (
        <DetailPermissionModal
          user={detailUser}
          menus={permMenus}
          initialPerms={permRows.filter(p => p.username === detailUser.username)}
          initialDetails={permDetailRows.filter(d => d.username === detailUser.username)}
          initialMenu={detailMenuKey}
          liveOverride={detailLiveOverride}
          onClose={() => { setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null); }}
          onSaved={() => {
            setDetailUser(null); setDetailMenuKey(null); setDetailLiveOverride(null);
            showMsg('상세설정이 저장되었습니다.'); loadMemberPerms();
          }}
        />
      )}
      {loginLogUser && (
        <LoginLogModal user={loginLogUser} onClose={() => setLoginLogUser(null)} />
      )}
      {securityAlerts.length > 0 && (
        <SecurityAlertModal alerts={securityAlerts} onClose={closeSecurityAlerts} />
      )}

      {/* 🔑 비밀번호 변경 모달 — 예전에는 화면 맨 아래 항상 펼쳐진 카드였으나, 헤더의
          "비밀번호 변경" 버튼(옛 "전용계정 관리" 버튼 자리)을 눌렀을 때만 열리는
          모달창으로 개편(2026-08). */}
      {showPwModal && (
        <Modal title="🔒 비밀번호 변경" onClose={() => setShowPwModal(false)}>
          <div>
            <label style={lbl}>현재 비밀번호</label>
            <input type="password" placeholder="현재 비밀번호" value={pwForm.currentPassword}
              onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })} style={inp} autoFocus />
            <label style={lbl}>새 비밀번호</label>
            <input type="password" placeholder="새 비밀번호 (6자 이상)" value={pwForm.newPassword}
              onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })} style={inp} />
            <label style={lbl}>새 비밀번호 확인</label>
            <input type="password" placeholder="새 비밀번호 다시 입력" value={pwForm.confirmPassword}
              onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && handleChangePw()} style={{ ...inp, marginBottom: 4 }} />
            <button onClick={handleChangePw} disabled={loading}
              style={{ width: '100%', marginTop: 10, padding: '10px 18px', background: loading ? '#aaa' : '#185FA5', color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? '변경 중...' : '비밀번호 변경'}
            </button>
          </div>
        </Modal>
      )}

    </div>
  );
}

// ── 🏢 회사정보 관리 — 2026-09-14: "환경설정" 메뉴 신설에 따라 이 모달(CompanyInfoModal)
// 전체가 frontend/src/pages/Settings.js로 이전되었다. 로직/필드는 100% 동일하며, 이
// 파일에는 더 이상 회사정보 관련 컴포넌트가 없다.
